import React from "react"; // 169 — react/react-in-jsx-scope is counted per JSX element; the rule's own remedy
import { Search, ChevronDown, FileText, FileType, Sheet } from "lucide-react"; // 195 §C.3b — the box's magnifier; 198 §C — the period shell's caret; 203 §A.2 — the export trio's icons (Reports' own)
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type Dispatch, type ReactNode, type SetStateAction, type RefObject } from "react";
import { foreignOf } from "../components/MoneyStack"; // 169 R7
import { ctc, pname, isFeeEarnerCollab } from "../utils";
import { roleLabel, useRoleLabels } from "../hooks/useRoleLabels";
import { resolveTaxonomyEntry, resolveTaxonomyLabel } from "../lib/taxonomyHelpers";
import { InitialsBadge, SegmentedControl, SortCaret } from "../components/SharedUI"; // 200-glance §M — the one sort caret; 198 §B — the drawers' toggle idiom (its outline, DRAWER_TOGGLE_STYLE, dropped here by 198-bis §B.2)
import { BASIS_BAR_BORDER } from "../components/CountersStrip"; // 190 round 11 [BA] — the strip's own bar, as a cell border
import { RECAP_MEMBERS, dueTone, type RecapMember } from "../lib/matterListCfg"; // 193 §C — the seven figures' own tones, the lists' definition
import { pctCo, ratCo } from "../lib/moneyTableCells"; // 199 §F — the rate and share faces, one definition with the matter recap
import type { Translate } from "../lib/interestRefusal"; // 198 §A.3 — the translator's one shape
import { FS, RAD } from "../lib/tokens";
import MenuSelect from "../components/MenuSelect"; // 154B §2
import FreeDateInput from "../components/FreeDateInput"; // 213B §15 — the free window's edges
import { computeCostsOverview, getFirmSettings, importOverheads, updateFirmSettings } from "../api";
import { periodBounds, PERIOD_OPTIONS, PERIOD_ALL_OPTIONS, type PeriodValue } from "../lib/periodRange"; // 198 §C.0 — the ONE period list and its ONE boundary copy
import { useTaxonomy, reload as reloadTaxonomy } from "../hooks/useTaxonomy"; // 203 §B.3 — the sheet re-reads its kind after the importer creates categories (the soft refetch: the rows stay on screen)
import { isDividerCode, pickEntryLabel } from "../utils/taxonomyMap"; // 199 §C — the activity columns: the taxonomy's codes, dividers out, labels in the reader's language
import type { TaxonomyEntry } from "@shared/entities";
import {
  redCostsMatter,
  redCostsEntry,
  redCostsCo,
  redRoleRows,
  redRecapInv, // 161 §10 — the recap's document basis rides the costs post
} from "../lib/financialsClient";
import {
  matterPassesProspect,
  prospectFilterOptions,
  type ProspectFilterValue,
  type ProspectTaggable,
} from "../lib/prospectFilter"; // 139 §3
import Table from "../components/Table";
import { TYPE_CHIP } from "../components/TypeTagBox"; // 204 §C — the activity headers wear the task-type chip, the one definition
import { asThemeVars } from "../lib/themeDerivation"; // 203-glance §C.1 — the day theme prop's seam
import { normaliseAppFont } from "../lib/appFonts"; // 203-glance-2 §C — the exporting user's face, narrowed at the seam
import { exportExcel, exportWord, printTable, type ExportDress } from "../lib/tableExport"; // 203 §A.2 — ONE export definition, Reports' (A.1 moved it here), read from this screen too; 203-glance §C — its dress
import { ImportTriggerButton } from "../components/ImportTriggerButton"; // 203 §B.2 — the house import trigger (the time-entry toolbars' own button)
import OverheadsImportModal, { type ImportApply } from "../components/modals/OverheadsImportModal"; // 203 §B.2–B.5 — the accountant's sheet in; 203-glance-2 §A — its apply shape
import { MONTHS, INT_DE, fcWhole, intFace, digitsOf, spreadEvenly, yearCell, yearColTotal, yearGrand, type OhCat, type OhSheetJson, type OhYear } from "../lib/overheadsSheet"; // 203 §B.2 — the sheet's pure helpers, moved out (the cure) so the importer reads the same definitions

/* 161 §9 — THE OVERHEADS SHEET (R9): a firm-level monthly budget by
   category (taxonomy kind `overhead_category`), typed ex-VAT in place and
   saved whole through PUT /api/firm-settings (`overheads`, admin-only like
   this screen). Shape: { "<year>": { "<code>": { "<month 1-12>": amount } } }.
   The cells are the IMPORT TARGET for the parked bookkeeping-provider
   adapter — nothing here may depend on a cell having been typed by hand.
   Module scope for the Bug-A rule: this component holds hooks.

   198 §E [RULED — Laurent: "round it to the euro everywhere, no cents, this
   is not real money but should be readable"] — WHOLE EUROS. E.1: the cells
   take the WhatsApp rate fields' shape (`type="text" inputMode="numeric"`,
   MessagesDrawer.tsx) so the browser draws no spinner nudging a rent by a
   cent; a typed cell is its digits and stores an INTEGER. E.2: the boxes and
   their type are sized for a dotted thousands figure (the values are in the
   commit). E.3 MEASURED: this file had TWO money faces — utils' `fc` (the
   reader's locale, currency style, two decimals: « 1 234,56 € » in fr-BE,
   read by this sheet alone) and CoView's `__fcCo` (de-DE grouping, zero
   decimals, « 1.234 € » — the three tables' own whole-euro face); the dot
   between thousands and no decimal part IS the tables' face, so it is HOISTED
   here as `fcWhole` and read by both — never a second formatter (`INT_DE` is
   the one Intl instance, the input face is the same digits without the sign).
   E.4: the TOTAL column stands FIRST, before the twelve months. E.5: THE
   TOTAL IS AN INPUT — (a) a typed total spreads EVENLY across the twelve
   months, (b) any month may then be edited by hand and the total follows,
   (c) a NEW total clears every edit and spreads again; E.5a [RULED]: the
   spread is computed in euros and the euros that do not divide by twelve go
   ONE EACH TO THE EARLIEST MONTHS, so the twelve always sum EXACTLY to what
   was typed (100 → 9 · 9 · 9 · 9 · 8 · 8 · 8 · 8 · 8 · 8 · 8 · 8). E.5b: THE
   TOTAL IS NOT STORED — the stored shape above KEEPS its months alone and the
   total is the row's SUM, read back from them; a later reader must not add a
   `total` field that could disagree with its own months. E.6: the year
   picker was already the house MenuSelect (never a native <select>) but wore
   no dress — a bare button; it takes the screen's field dress. */
/* the field dress of the sheet's boxes: the app's field, monospaced, right-aligned, sized
   for a dotted figure (E.2 — the numbers are measured in the commit) */
const OH_MONTH_BOX: CSSProperties = { width: 84, margin: 0, padding: "2px 4px", textAlign: "right", fontFamily: "'JetBrains Mono',monospace", fontSize: FS.s11 };
const OH_TOTAL_BOX: CSSProperties = { ...OH_MONTH_BOX, width: 96, fontWeight: 600 };
const OverheadsSheet = ({ t, sheet, setSheet, firmName, dayTheme, appFont, onSaved }: {
  /* 200 §G.5 — the IMPORTED shape (197B §C's rule for a translator prop): a
     function type with named parameters is two `no-unused-vars` warnings. */
  t: Translate;
  sheet: OhSheetJson | null;
  setSheet: Dispatch<SetStateAction<OhSheetJson | null>>;
  /** 203 §A.2.4 — the documents' header; "" lets the exporters' own fallback stand */
  firmName: string;
  /** 203-glance §C.1 — the firm's DAY theme's vars, for the documents' palette (App's `dayThemeVarsOf`) */
  dayTheme: Record<string, string> | null;
  /** 203-glance-2 §C — the exporting user's text face (App's `appFont`), worn by the documents */
  appFont: string;
  /** 213 §9 — a save landed: the costs compute (which reads the sheet from the database) asks again */
  onSaved: () => void;
}) => {
  /* 203-glance §A — WITH the archived rows (the arm App.tsx takes for activity_code): the importer must SEE an
     archived category to refuse a row naming it, instead of creating a second one under the same label. The sheet
     itself keeps rendering the living ones alone — `cats` below filters `archived` out as it always did. */
  const { entries: ohCats, lang: ohLangRaw } = useTaxonomy("overhead_category", { includeArchived: true });
  // 203 §B.3 — the reader's language, narrowed once at the seam (the hook is any-typed): a category the importer creates is labelled under it too
  const ohLang = typeof ohLangRaw === "string" ? ohLangRaw : "en";
  // 203 §B.2 — the file the reader picked, until the importer closes
  const [importFile, setImportFile] = useState<File | null>(null);
  const importRef = useRef<HTMLInputElement>(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "failed">("idle");
  /* 200 §G.4 — FORMAT ON BLUR, and why: every box here is controlled by the
     stored figure's FACE (`intFace`: de-DE grouping, « 125.000 »), so each
     keystroke re-rendered a string that differed from what the DOM held and
     React put the caret at the end — an edit in the middle of « 125.000 »
     jumped. The chosen cure keeps ONE draft, the focused box's: while a box
     has focus it shows the text as typed (the digits, whatever the user
     wrote), the value still commits on every keystroke (a typed total still
     spreads live, a month still moves the total), and the grouping returns
     when the box is left. Chosen over caret-preservation (re-placing the
     selection after each format through a ref and a layout effect) because a
     focused box that shows what you type has no caret to preserve — and the
     198 E.0 rule ("a typed cell is its digits") is what it shows. The same
     mechanism serves the twelve month boxes, which had the same jump. */
  const [draft, setDraft] = useState<{ key: string; text: string } | null>(null);
  const faceOf = (key: string, face: string) => (draft?.key === key ? draft.text : face);
  // The sheet STATE lives in CoView (it also rides the costs compute post);
  // this ref mirrors the latest value for the blur save.
  const sheetRef = useRef<Record<string, Record<string, Record<string, number>>>>({});
  useEffect(() => { sheetRef.current = sheet || {}; }, [sheet]);
  const nowYear = new Date().getFullYear();
  const years = Array.from(
    new Set([
      ...Object.keys(sheet || {}),
      String(nowYear - 1), String(nowYear), String(nowYear + 1),
    ]),
  ).sort();
  const cats = [...(ohCats || [])]
    .filter((e: OhCat) => !e.archived)
    .sort((a: OhCat, b: OhCat) => (a.order ?? 0) - (b.order ?? 0));
  // 203-glance §A — the archived categories, for the importer alone (typed once at the seam: the hook is any-typed)
  const ohArchived = ((ohCats ?? []) as OhCat[]).filter((e) => e.archived === true);
  // 203 §B.5 — the cell, the column total and the grand read the lib's definitions, which the importer's preview reads too
  const cellOf = (code: string, m: number): number | null => yearCell(sheet?.[year], code, m);
  /* one writer for a category's twelve cells: `mut` receives a COPY of the
     row's month map and returns the next one; an empty map deletes the row,
     an empty year deletes the year — the stored shape never grows a key it
     does not need. */
  const writeRow = (code: string, mut: (c: Record<string, number>) => Record<string, number>) => {
    setSheet((prev) => {
      const next: OhSheetJson = { ...(prev || {}) };
      const y = { ...(next[year] || {}) };
      const c = mut({ ...(y[code] || {}) });
      if (Object.keys(c).length) y[code] = c; else delete y[code];
      if (Object.keys(y).length) next[year] = y; else delete next[year];
      sheetRef.current = next;
      return next;
    });
  };
  // E.5(b) — a month typed by hand: the cell alone moves; the total follows as the sum
  const setCell = (code: string, m: number, raw: string) => {
    const n = digitsOf(raw);
    writeRow(code, (c) => {
      if (n == null) delete c[String(m)]; else c[String(m)] = n;
      return c;
    });
  };
  // E.5(a)(c) — a total typed: the row is REPLACED by the even spread (every
  // manual edit goes); an emptied total empties the row
  const setRowTotal = (code: string, raw: string) => {
    const n = digitsOf(raw);
    writeRow(code, () => {
      const c: Record<string, number> = {};
      if (n == null) return c;
      spreadEvenly(n).forEach((v, i) => { c[String(i + 1)] = v; });
      return c;
    });
  };
  const save = () => {
    setSaveState("saving");
    const put = updateFirmSettings({ overheads: sheetRef.current });
    put.then(() => { setSaveState("saved"); onSaved(); }).catch(() => setSaveState("failed"));
    return put;
  };
  /* 203 §B — THE IMPORTER'S WRITE IS THE SHEET'S OWN: the year's map replaces
     the year on the client-held sheet (an empty map deletes the year, as
     writeRow's rule), the whole object goes through the one PUT above (B.0: the
     SAME object through the SAME route, nothing else written), and the kind is
     re-read so the categories the importer created render; the save state
     beside the year picker says whether the PUT landed, as for a typed cell.
     203-glance-2 §A [RULED] — WITH CATEGORIES TO RESTORE, the same object goes
     through `PUT /api/firm-settings/overheads` instead, which un-archives the
     ticked categories and writes the sheet in ONE server transaction (never
     two round trips, never a restore that survives a failed write); the kind
     is re-read once the write has landed, so a restored category renders —
     and a plain import (nothing to restore) keeps the ordinary PUT. */
  const applyImport = ({ year: nextYear, restore }: ImportApply) => {
    const all: OhSheetJson = { ...sheetRef.current };
    if (Object.keys(nextYear).length) all[year] = nextYear;
    else delete all[year];
    sheetRef.current = all;
    setSheet(all);
    let put: Promise<unknown>;
    if (restore.length) {
      setSaveState("saving");
      put = importOverheads({ overheads: all, restoreCodes: restore });
      put
        .then(() => {
          setSaveState("saved");
          onSaved();
        })
        .catch(() => {
          setSaveState("failed");
        });
    } else {
      put = save();
    }
    void put.finally(() => {
      reloadTaxonomy("overhead_category");
    });
  };
  // E.5b — the total is the row's SUM, never a stored field
  const rowHas = (code: string) => MONTHS.some((m) => cellOf(code, m) != null);
  const rowTotal = (code: string) => MONTHS.reduce((s, m) => s + (cellOf(code, m) || 0), 0);
  const catCodes = cats.map((e: OhCat) => e.code);
  const colTotal = (m: number) => yearColTotal(sheet?.[year], catCodes, m);
  const grand = yearGrand(sheet?.[year], catCodes);
  const totalWord = t("costs.oh.total", { fallback: "TOTAL" });
  /* 203 §A.2.4 — THE SHEET'S EXPORT, from the one definition, for the year on
     screen: categories down (the label as the row shows it), TOTAL then the
     twelve months across (E.4's order). THE EXCEL IS §B.1'S TEMPLATE — "there
     is no second exporter" — so it carries the FIGURES AS NUMBERS (the stored
     whole euros, the row's total as its sum — E.5b: read back from the
     months, never a stored field; an empty cell stays empty) under the one
     header row the importer will look for: ROW 1 = « Catégorie » · « TOTAL »
     · 1 … 12 (this screen's own words, `costs.oh.category` and
     `costs.oh.total`, in the reader's language). No totals row in the Excel
     (the shape ReportsView passes, and a sheet the accountant fills must not
     carry a row that reads as a category). Word and print carry the faces the
     boxes show (`intFace`, de-DE grouping) and the totals row last, as on
     screen (`fcWhole`). The year rides the title, so the file says which year
     it is a template for. */
  const ohTitle = `${t("nav.costs")} — ${t("costs.section.overheads", { fallback: "Overheads" })} ${year}`;
  const ohHeaders: string[] = [t("costs.oh.category", { fallback: "Category" }), totalWord, ...MONTHS.map(String)];
  const ohRightCols = ohHeaders.map((_, i) => i).filter((i) => i >= 1);
  const ohRowsNumeric = (): (string | number | null)[][] =>
    cats.map((e: OhCat) => [
      String(resolveTaxonomyLabel(e.code, cats)),
      rowHas(e.code) ? rowTotal(e.code) : null,
      ...MONTHS.map((m) => cellOf(e.code, m)),
    ]);
  const ohRowsFaced = (): string[][] =>
    cats.map((e: OhCat) => [
      String(resolveTaxonomyLabel(e.code, cats)),
      intFace(rowHas(e.code) ? rowTotal(e.code) : null),
      ...MONTHS.map((m) => intFace(cellOf(e.code, m))),
    ]);
  // `colTotal` is `any` (the taxonomy's entries are), typed once here at the seam
  const ohTotalRow: (string | null)[] = [totalWord, fcWhole(grand), ...MONTHS.map((m) => fcWhole(colTotal(m) as number))];
  // 203-glance §C — the dress: the day theme, the figure columns, the TOTAL column bold as on screen (OH_TOTAL_BOX)
  const ohDress: ExportDress = { theme: dayTheme, font: appFont, rightCols: ohRightCols, boldCols: [1] };
  const ohExport: ExportHandlers = {
    pdf: () => { printTable(ohTitle, ohHeaders, ohRowsFaced(), ohTotalRow, firmName, ohDress); },
    word: () => { void exportWord(ohTitle, ohHeaders, ohRowsFaced(), firmName, ohTotalRow, ohDress); },
    excel: () => { void exportExcel(ohTitle, ohHeaders, ohRowsNumeric(), null, ohDress); }, // §D — no totals row: the template's row-1 contract
  };
  if (sheet == null) return null;
  return (
    <div style={{ marginTop: 18 }}>
      <h3
        style={{
          fontSize: FS.s12,
          marginBottom: 8,
          color: "var(--tp)",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        {t("costs.section.overheads", { fallback: "Overheads" })}
        {/* E.6 — the house MenuSelect. 198-bis §D [RULED — Laurent, 2026-09-20:
            "less high, consistent with the period pickers on the same page"]:
            MEASURED in Chromium on the running staging (root zoom 1) — this
            picker's border box 30 px (`.field-input`'s 30 px `min-height`
            floor beat the inline `height: 22`), the period picker's 24 px. It
            now wears the period picker's OWN shell (`.search-box teActBox`,
            198 §C.1: the paint-nothing MenuSelect face over the app's menu,
            its ChevronDown) — the same construction, no third number; the
            face resets the h3's weight so it reads as the period picker does. */}
        <div className="search-box teActBox">
          <MenuSelect
            value={year}
            onChange={(v: string) => setYear(v)}
            options={years.map((y) => [y, y] as const)}
            caret={false}
            intrinsicWidth
            style={{
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
              width: "100%",
              minWidth: 0,
              height: "auto",
              color: "var(--tp)",
              fontSize: "var(--fs-11)",
              fontFamily: "inherit",
              fontWeight: 400,
            }}
          />
          <ChevronDown size={12} style={{ color: "var(--tm)", flexShrink: 0 }} />
        </div>
        {saveState !== "idle" && (
          <span style={{ fontSize: FS.s9_5, fontWeight: 400, color: saveState === "failed" ? "var(--rd)" : "var(--tm)" }}>
            {saveState === "saving"
              ? t("costs.oh.saving", { fallback: "Saving…" })
              : saveState === "saved"
                ? t("costs.oh.saved", { fallback: "Saved" })
                : t("costs.oh.save_failed", { fallback: "Could not save — retry the cell" })}
          </span>
        )}
        <ExportButtons t={t} on={ohExport} pending={false} />
        {/* 203 §B.2 — THE SHEET IN: the house import trigger beside the export
            trio (the time-entry toolbars' own button), its hover the template
            rule in one sentence; the file goes to the importer below. */}
        <span
          title={t("costs.oh.import_hint", {
            fallback: "Import a sheet exported from here (row 1: Category · TOTAL · 1 … 12): every row is a category, created if unknown; a TOTAL is spread evenly over the twelve months; months alone are taken as given. Nothing is written before you confirm.",
          })}
          style={{ display: "inline-flex", fontWeight: 400 }}
        >
          <ImportTriggerButton teFileRef={importRef} t={t} />
        </span>
        <input
          ref={importRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={(ev) => {
            setImportFile(ev.target.files?.[0] ?? null);
            ev.target.value = "";
          }}
        />
      </h3>
      {importFile && (
        <OverheadsImportModal
          t={t}
          lang={ohLang}
          file={importFile}
          year={year}
          cats={cats as OhCat[]}
          archived={ohArchived}
          current={sheet[year] ?? {}}
          onClose={() => setImportFile(null)}
          onApply={applyImport}
        />
      )}
      <div className="table-wrap" style={{ overflowX: "auto" }}>
        <Table className="costs-table" style={{ minWidth: 0, fontSize: FS.s10, borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "4px 6px", color: "var(--tm)", fontSize: FS.s9 }}>
                {t("costs.oh.category", { fallback: "Category" })}
              </th>
              {/* E.4 — the total column FIRST */}
              <th style={{ textAlign: "right", padding: "4px 6px", color: "var(--tp)", fontSize: FS.s9, fontWeight: 700 }}>
                {totalWord}
              </th>
              {MONTHS.map((m) => (
                <th key={m} style={{ textAlign: "right", padding: "4px 4px", color: "var(--tm)", fontSize: FS.s9 }}>{m}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cats.map((e: OhCat) => (
              <tr key={e.code} style={{ borderTop: "1px solid var(--br)" }}>
                <td style={{ padding: "3px 6px", whiteSpace: "nowrap", color: "var(--ts)" }}>
                  {resolveTaxonomyLabel(e.code, cats)}
                </td>
                <td style={{ padding: "2px 4px", textAlign: "right" }}>
                  {/* E.5 — the total as an input: shows the row's sum, and a
                      typed total spreads (E.5a) */}
                  <input
                    className="field-input"
                    type="text"
                    inputMode="numeric"
                    aria-label={totalWord}
                    title={t("costs.oh.total_input_title", {
                      fallback: "Type a total: it is spread evenly over the twelve months, the euros that do not divide going one each to the earliest months; edit a month and the total follows; a new total clears every edit.",
                    })}
                    value={faceOf("total:" + e.code, intFace(rowHas(e.code) ? rowTotal(e.code) : null))}
                    onChange={(ev) => {
                      setDraft({ key: "total:" + e.code, text: ev.target.value });
                      setRowTotal(e.code, ev.target.value);
                    }}
                    onBlur={() => { setDraft(null); void save(); }}
                    style={OH_TOTAL_BOX}
                  />
                </td>
                {MONTHS.map((m) => (
                  <td key={m} style={{ padding: "2px 2px", textAlign: "right" }}>
                    <input
                      className="field-input"
                      type="text"
                      inputMode="numeric"
                      aria-label={String(m)}
                      value={faceOf(`m:${e.code}:${String(m)}`, intFace(cellOf(e.code, m)))}
                      onChange={(ev) => {
                        setDraft({ key: `m:${e.code}:${String(m)}`, text: ev.target.value });
                        setCell(e.code, m, ev.target.value);
                      }}
                      onBlur={() => { setDraft(null); void save(); }}
                      style={OH_MONTH_BOX}
                    />
                  </td>
                ))}
              </tr>
            ))}
            <tr style={{ borderTop: "2px solid var(--br)" }}>
              <td style={{ padding: "4px 6px", fontWeight: 700, color: "var(--tp)" }}>
                {totalWord}
              </td>
              <td style={{ padding: "4px 6px", textAlign: "right", fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: "var(--ac)", whiteSpace: "nowrap" }}>
                {fcWhole(grand)}
              </td>
              {MONTHS.map((m) => (
                <td key={m} style={{ padding: "4px 4px", textAlign: "right", fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {fcWhole(colTotal(m))}
                </td>
              ))}
            </tr>
          </tbody>
        </Table>
      </div>
    </div>
  );
};

/* 199 §B.0 [RULED — Laurent: "less spacing between both"] — THE REF COLUMN IS
   AS WIDE AS THE WIDEST REF. MEASURED on the running staging: `.sticky-col-1`
   was a fixed 80 px (App.css) while « SMK-01 » needs 40, so the title began
   ~40 px after the ref ended on every row — and `.sticky-col-2`'s `left` was
   that same 80, because a sticky offset cannot read its neighbour's width in
   CSS. So the width is MEASURED once the rows are on screen: the widest ref
   text (each ref wrapped in `span.costs-ref`) plus the cell's 6 + 6 px
   padding, written on the table as `--costs-col1-w`, which App.css reads for
   BOTH the first column's width and the second column's offset — a firm with
   long refs gets a wider column, one with short refs a tighter one, and the
   two columns stay aligned by construction. `offsetWidth` (layout px), never
   the zoomed rect (142's lesson). The case table and the case × activity
   table (§C.1) share it: one hook, two mounts.
   MEASURED on the running staging, first run: the column laid out at 104 px
   against a written 84 — « TOTAL GÉNÉRAL » in the total row's first cell,
   nowrap, forced the column wider than any ref, and had done so under the
   old fixed 80 too (the title column's offset was 80 while the ref column
   was 104: the two overlapped under a horizontal scroll). Two answers, both
   applied: the total row's label now SPANS the two fixed columns (one cell,
   `sticky-col-12`, "Total général (23 dossiers)" on one line — nothing in
   the ref column but refs and its heading), and the hook reads the column's
   laid-out width back after writing and writes THAT if it is wider, so the
   title column's offset is the ref column's real width whatever forced it. */
const REF_CELL_PADDING = 12;
const useRefColumnWidth = (tableRef: RefObject<HTMLTableElement>, key: string) => {
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    let max = 0;
    table.querySelectorAll<HTMLElement>("tbody td.sticky-col-1 > span.costs-ref").forEach((el) => {
      if (el.offsetWidth > max) max = el.offsetWidth;
    });
    if (max <= 0) return;
    table.style.setProperty("--costs-col1-w", `${String(Math.ceil(max) + REF_CELL_PADDING)}px`);
    const laid = table.querySelector<HTMLElement>("tbody td.sticky-col-1");
    if (laid && laid.offsetWidth > Math.ceil(max) + REF_CELL_PADDING) table.style.setProperty("--costs-col1-w", `${String(laid.offsetWidth)}px`);
  }, [tableRef, key]);
};

/* 199 §C — THE FACES AND THE DRESS THE THREE TABLES SHARE, hoisted from CoView
   unchanged (a move, not a rewrite) so the activity table below — a module-scope
   component, two mounts — reads the same ones. CoView keeps its names as aliases. */
// 203 §A.2.2 — a flex row: the heading's word, then the table's export trio at the right
const SECTION_H3: CSSProperties = { fontSize: FS.s12, marginBottom: 8, color: "var(--tp)", fontWeight: 600, display: "flex", alignItems: "center", gap: 10 };
/* 203 §A.2 [RULED — Laurent, 2026-09-21: "not to double the feature, it is to
   make it available while glancing instead of needing to go to Reports section
   and modifying the filters there again"] — THE THREE EXPORT BUTTONS, ONCE, for
   the five tables of this screen. The trio is Reports' own (ReportsView's three
   `btn btn-ghost` buttons: the same icons, the same keys, the same dress, the
   same hold while the server rows have not landed — C.2's guard), so the two
   surfaces read alike; Reports keeps its own three. [JUDGEMENT — advisor,
   overrulable] each table carries the trio in ITS OWN header, never one set for
   the screen: the five are exported separately because they are read
   separately. A table's export carries WHAT IS ON SCREEN — the period, the
   matter filters and the sort as the reader left them — which is the whole
   point of exporting from here rather than from Reports. Hookless, module
   scope. */
interface ExportHandlers { pdf: () => void; word: () => void; excel: () => void }
/** the firm's name off the served settings, for the documents' header; "" lets the exporters' own fallback stand */
const firmNameOf = (firmSettings: unknown): string => {
  const name = (firmSettings as { firm?: { name?: unknown } } | null | undefined)?.firm?.name;
  return typeof name === "string" ? name : "";
};
const EXPORT_BTN: CSSProperties = { padding: "3px 8px", fontSize: FS.s10 };
const EXPORT_ICON: CSSProperties = { marginRight: 3, verticalAlign: "middle" };
const ExportButtons = ({ t, on, pending }: { t: Translate; on: ExportHandlers; pending: boolean }) => {
  const st: CSSProperties = pending ? { ...EXPORT_BTN, opacity: 0.5 } : EXPORT_BTN;
  return (
    <span style={{ marginLeft: "auto", display: "inline-flex", gap: 4, fontWeight: 400 }}>
      <button type="button" className="btn btn-ghost" style={st} disabled={pending} onClick={on.pdf} title={t("reports.export_pdf", { fallback: "Export PDF" })}>
        <FileText size={12} style={EXPORT_ICON} />
        {t("reports.pdf", { fallback: "PDF" })}
      </button>
      <button type="button" className="btn btn-ghost" style={st} disabled={pending} onClick={on.word} title={t("reports.export_word", { fallback: "Export Word" })}>
        <FileType size={12} style={EXPORT_ICON} />
        {t("reports.word", { fallback: "Word" })}
      </button>
      <button type="button" className="btn btn-ghost" style={st} disabled={pending} onClick={on.excel} title={t("reports.export_excel", { fallback: "Export Excel" })}>
        <Sheet size={12} style={EXPORT_ICON} />
        {t("reports.excel", { fallback: "Excel" })}
      </button>
    </span>
  );
};
const TH_ST: CSSProperties = {
  cursor: "pointer",
  userSelect: "none",
  fontSize: FS.s9_5,
  fontWeight: 600,
  color: "var(--tm)",
  textTransform: "uppercase",
  letterSpacing: ".3px",
  padding: "6px 8px",
  /* 158 §6 — the header rule is INLINE here, so App.css's `thead th`
     never reaches it: measured in the running app at 1.03 : 1 against
     this table's own surface, i.e. invisible. `var(--rule)` is the
     derived divider — see :root in App.css. */
  borderBottom: "1px solid var(--rule)",
  whiteSpace: "nowrap",
  textAlign: "left",
};
const TH_ST_R: CSSProperties = { ...TH_ST, textAlign: "right" };
const TD_R: CSSProperties = {
  textAlign: "right",
  padding: "5px 8px",
  whiteSpace: "nowrap",
};
const TD_L: CSSProperties = {
  textAlign: "left",
  padding: "5px 8px",
  whiteSpace: "nowrap",
};
// 199 §F — `pctCo` / `ratCo` live in lib/moneyTableCells now (the matter recap
// prints the same faces); imported above.
const hCo = (min: any) => {
  if (!min) return "—";
  return (min / 60).toFixed(1).replace(".", ",") + "h";
};
/* 199 §C.2 / §D — the collaborator tables' fixed part, ONCE: the name cell (the
   photo-capable badge, the name) and the role cell, as the overview always drew
   them — the collaborators × activities table wears the same two so the two
   tables read as one list (the brief's "same names column as the overview table
   below it"). 200-glance §L: the badge is every badge's — `--cb` fill, `--bf`
   text, the `--ac` ring on the viewer's own row (`isMe`); the local grey
   palette it once wore is gone. */
interface CollabLike {
  id: number;
  role?: string | null;
  initials?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  title?: string | null;
  name?: string | null;
}
const collabFixedCells = (collab: CollabLike, viewerId: number | null) => (
  <>
    <td className="sticky-col-name" style={TD_L}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <InitialsBadge
          noDim /* 75 §3 — costs is an invoicing surface: archived renders normally */
          person={collab}
          isMe={collab.id === viewerId}
          size={18}
          fontSize={8.5}
        />
        <span>{pname(collab)}</span>
      </span>
    </td>
    <td style={TD_L}>
      {collab.role ? (
        <span className="tag" style={{ fontSize: FS.s9 }}>
          {roleLabel(collab.role)}
        </span>
      ) : (
        <span style={{ color: "var(--tm)" }}>—</span>
      )}
    </td>
  </>
);

/* 199 §C.0 [RULED — Laurent, 2026-09-20] — ONE CELL SHAPE, DEFINED ONCE. Both
   activity tables carry one column per activity type, and each cell holds
   THREE FIGURES STACKED ON THREE LINES, never three columns:

         12,5 h        the hours
        1.062 €        what those hours cost — each entry at the internal rate
                       in force on ITS OWN date (§A's history; activityBlock,
                       caseFinancials.js)
           18 %        the share of the ROW'S OWN hours in the filtered window

   ("so one can know how much time the firm spent in eg that week on reading
   correspondence or meetings, or how much time the collaborator spends on one
   activity compared to other activities"). C.0a [RULED — option (a)]: EVERY
   activity type, always — the columns are the full `activity_code` taxonomy in
   its own order whether or not the window holds hours for one, so a column
   never moves when the period changes; a type with no hours renders an EMPTY
   cell, not zeros; the table scrolls in the screen's `.table-wrap`. C.0b: one
   component, two mounts — the rows are matters (§C.1, under the case table)
   or collaborators (§C.2, above the overview); the fixed part and the totals
   row's head come from the host, the cells never. The figures are the
   server's (`activity` on each served row, `caseActivityTotals` /
   `collabActivityTotals`); nothing is computed here. */
interface ActivityCell { min?: number | null; cost?: number | null; pct?: number | null }
interface ActivityBlock { totalMin?: number | null; byType?: Record<string, ActivityCell | undefined> | null }
interface ActivityType { code: string; label: string }
/* 200 §C.1 [RULED — Laurent, 2026-09-20] — THE ACTIVITY TABLES SORT, on the
   model of `__cycleSort` / `__cycleCSort` below: a heading pressed sorts
   ascending, pressed again descending; the totals row stays first. The FIXED
   columns sort as TEXT on the value the host names beside each cell
   (`fixedSort`: the ref and the title; the name and the role) — the host still
   gives the cells, never this component (C.0b); EVERY ACTIVITY COLUMN SORTS ON
   THE CELL'S HOURS, the first of the three stacked lines [JUDGEMENT — advisor,
   Laurent may say amount instead]; an empty cell sorts as nothing (−∞, the
   overview's own rule for a null). No heading pressed = the host's order (the
   case table's, the overview's), as the tables read before this. ONE
   component, TWO mounts, each with its own sort state — so « Par activité »
   gained it in the same commit. */
interface ActivityFixedHead { key: string; label: ReactNode; className?: string }
/* 203 §A.2.2 — `text`: the fixed cells as the export writes them (the ref and the
   whole title; the name and the role's label) — beside `fixedSort`, which sorts
   (the role's CODE), and `fixed`, which renders (the badge). Three readings of
   the two cells, each its own. */
interface ActivityRow { key: string; fixed: ReactNode; fixedSort: string[]; text: string[]; activity: ActivityBlock | null; onClick?: () => void }
interface ActivitySort { k: string; d: 1 | -1 }
const sortActivityRows = (rows: ActivityRow[], sort: ActivitySort | null): ActivityRow[] => {
  if (!sort) return rows;
  const fixedIdx = sort.k.startsWith("fixed:") ? Number(sort.k.slice(6)) : -1;
  const code = sort.k.slice(4);
  const minOf = (r: ActivityRow): number => r.activity?.byType?.[code]?.min ?? -Infinity;
  return [...rows].sort((a, b) =>
    fixedIdx >= 0
      ? sort.d * (a.fixedSort[fixedIdx] ?? "").localeCompare(b.fixedSort[fixedIdx] ?? "")
      : sort.d * (minOf(a) - minOf(b)),
  );
};
/* 203 §A.2.2 — AN ACTIVITY TABLE'S EXPORT, from the one definition: the rows in
   the order the reader left (`sortActivityRows` over the host's sort — the very
   function the table sorts with), per row the two fixed cells as text and one
   cell per activity column, the three stacked figures on ONE line, " · "
   between (hours · cost · share), empty where the cell is empty, as on screen.
   The totals row (the table's first) travels last, its label spanning the two
   fixed columns as it does on screen (Word and print; Excel takes the rows
   alone — the shape ReportsView passes). Pure, module scope. */
const activityCellText = (cell: ActivityCell | undefined): string =>
  cell ? `${hCo(cell.min)} · ${fcWhole(cell.cost)} · ${pctCo(cell.pct)}` : "";
interface ActivityExportSpec {
  title: string; fixedLabels: string[]; totalLabel: string; types: ActivityType[];
  rows: ActivityRow[]; sort: ActivitySort | null; totals: ActivityBlock | null; firmName: string;
  /** 203-glance §C.1 — the firm's day theme's vars, for the documents' palette */
  dayTheme: Record<string, string> | null;
  /** 203-glance-2 §C — the exporting user's text face */
  appFont: string;
  /** 204 §D — the heading of the KEY under the table (« Activity codes »): every code with its full name */
  legendTitle: string;
}
const activityExport = ({ title, fixedLabels, totalLabel, types, rows, sort, totals, firmName, dayTheme, appFont, legendTitle }: ActivityExportSpec): ExportHandlers => {
  /* 204 §C [RULED — Laurent: "same in both, these are working documents for
     insiders only"] — the exported heads are the CODES, as the screen's chips
     are; a file has no hover, and the ruling names the readers who need none.
     This is the header's source for all three formats (not the `<th>`). */
  const headers = [...fixedLabels, ...types.map((ty) => ty.code)];
  const rightCols = types.map((_, i) => fixedLabels.length + i);
  const body = () => sortActivityRows(rows, sort).map((r) => [...r.text, ...types.map((ty) => activityCellText(r.activity?.byType?.[ty.code]))]);
  const totalRow: (string | null)[] = [totalLabel, null, ...types.map((ty) => activityCellText(totals?.byType?.[ty.code]))];
  /* 204 §D — the fixed columns are the KEY every block repeats, and the key under the table decodes the codes
     the heads carry since §C (a file has no hover). */
  const dress: ExportDress = { theme: dayTheme, font: appFont, rightCols, keyCols: fixedLabels.length, legend: { title: legendTitle, rows: types.map((ty) => [ty.code, ty.label] as const) } }; // 203-glance §C · 203-glance-2 §C · 204 §D
  return {
    pdf: () => { printTable(title, headers, body(), totalRow, firmName, dress); },
    word: () => { void exportWord(title, headers, body(), firmName, totalRow, dress); },
    excel: () => { void exportExcel(title, headers, body(), totalRow, dress); }, // §D — the totals row, in the Excel too
  };
};
const ActivitySortInd = ({ sort, k }: { sort: ActivitySort | null; k: string }) => (
  <SortCaret active={sort?.k === k} d={sort?.d} /> // 200-glance §M.2 — the one sort caret; the dim ▶ holds the space
);
const ActivityCellView = ({ cell, title }: { cell: ActivityCell | undefined; title: string }) => (
  <td className="mono" style={{ ...TD_R, verticalAlign: "top", lineHeight: 1.3 }} title={cell ? title : undefined}>
    {cell ? (
      <>
        <div>{hCo(cell.min)}</div>
        <div style={{ color: "var(--ts)" }}>{fcWhole(cell.cost)}</div>
        <div style={{ color: "var(--tm)", fontSize: FS.s9_5 }}>{pctCo(cell.pct)}</div>
      </>
    ) : null}
  </td>
);
const ActivityTable = ({ tableRef, types, fixedHeads, totalHead, rows, totals, cellTitle, emptyText, sort, onSort }: {
  tableRef?: RefObject<HTMLTableElement>;
  types: ActivityType[];
  /** the fixed columns' headings, sortable as text on the row's `fixedSort` */
  fixedHeads: ActivityFixedHead[];
  totalHead: ReactNode;
  rows: ActivityRow[];
  totals: ActivityBlock | null;
  cellTitle: string;
  emptyText: string;
  /** 203 §A.2.2 — the sort is the HOST's state now (one per mount, as before),
      so the host's export can carry the order the reader left; null is the
      host's order. */
  sort: ActivitySort | null;
  onSort: Dispatch<SetStateAction<ActivitySort | null>>;
}) => {
  // 200 §C.1 — a heading pressed sorts ascending, pressed again descending.
  const cycle = (k: string) => {
    onSort((p) => (p?.k === k ? { k, d: p.d === 1 ? -1 : 1 } : { k, d: 1 }));
  };
  const sorted = sortActivityRows(rows, sort);
  return (
  <div className="table-wrap" style={{ overflowX: "auto" }}>
    <Table ref={tableRef} className="costs-table" style={{ minWidth: 0, fontSize: FS.s10, borderCollapse: "collapse" }}>
      <thead>
        <tr>
          {fixedHeads.map((h, i) => (
            <th key={h.key} className={h.className} style={TH_ST} onClick={() => { cycle("fixed:" + String(i)); }}>
              {h.label}
              <ActivitySortInd sort={sort} k={"fixed:" + String(i)} />
            </th>
          ))}
          {types.map((ty) => (
            /* 204 §C [RULED — Laurent: "replace the activity headers text by
               the corresponding badge, will be much smaller, showing the full
               name at hover"] — INVERTED: the CODE is the chip, the full name
               is the hover. What stood here wrapped the phrase ("a heading may
               wrap: twenty-seven types side by side … the code is the hover")
               under `maxWidth: 120`; the chip is the task-type chip every task
               row already wears (`TYPE_CHIP`, TypeTagBox.tsx — EisenTaskCard's,
               the code in JetBrains Mono on `--as`, `title` the description),
               one definition, not a fifth copy. The hover sits on the `<th>`
               so the name shows wherever the pointer rests in the cell. */
            <th key={ty.code} style={{ ...TH_ST_R, verticalAlign: "bottom" }} title={ty.label} onClick={() => { cycle("act:" + ty.code); }}>
              <span style={TYPE_CHIP}>{ty.code}</span>
              <ActivitySortInd sort={sort} k={"act:" + ty.code} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr
          className="costs-table-total"
          /* 195B §B — the ground is the class rule's `var(--as)` (App.css) */
          style={{ borderBottom: "2px solid var(--ac)", fontWeight: 700 }}
        >
          {totalHead}
          {types.map((ty) => (
            <ActivityCellView key={ty.code} cell={totals?.byType?.[ty.code]} title={cellTitle} />
          ))}
        </tr>
        {sorted.map((r) => (
          <tr key={r.key} onClick={r.onClick} style={r.onClick ? { cursor: "pointer" } : undefined}>
            {r.fixed}
            {types.map((ty) => (
              <ActivityCellView key={ty.code} cell={r.activity?.byType?.[ty.code]} title={cellTitle} />
            ))}
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={fixedHeads.length + types.length} style={{ textAlign: "center", padding: "20px", color: "var(--tm)", fontStyle: "italic" }}>
              {emptyText}
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  </div>
  );
};

/* B.6 drain — extracted from App.tsx as a proper module component (the
   ruled C.1 fix): defined inline in App's render body, its 8 useState hooks
   remounted on every App re-render, resetting the user's Costs filters and
   sorts (pass-12b / CLAUDE.md "CoView inline-component anti-pattern").
   Module scope gives it stable identity; the closed-over App state rides in
   as props. */
const CoView = ({
  _appIsAdmin,
  _appPerms,
  t: _tAny,
  firmSettings, // 198 §C — the period picker's axis input (D.3): the accounting year reads the firm's start off it
  currentUser: _currentUserAny, // 196 §C.2 — the presented identity, for the badge's `isMe` (the list's)
  cases,
  collectiveCases,
  timeEntries,
  collaborators,
  invoices, // 161 §10 — the served invoice rows the recap columns read
  nav,
  caseTypesList,
  caseTypeEntries,
  caseStatusEntries,
  dayTheme: _dayThemeAny, // 203-glance §C.1 — the firm's DAY theme's vars (App's dayThemeVarsOf), for the documents' palette
  appFont: _appFontAny, // 203-glance-2 §C — the exporting user's text face (App's appFont), for the documents
}: any) => {
    // 203-glance §C.1 — the day theme, narrowed once at the seam
    const dayTheme = asThemeVars(_dayThemeAny);
    // 203-glance-2 §C — the face, narrowed once at the seam: `normaliseAppFont` takes unknown and answers a family this build renders
    const appFont = normaliseAppFont(_appFontAny);
    /* 198 §A.3 — THE TRANSLATOR, TYPED ONCE AT THE SEAM (181's rule for a file
       whose props arrive as `any`: narrow `t` for the WHOLE file rather than
       per call — every literal `t(…)` below stays visible to i18n-keys-complete
       and stops costing the ratchet an unsafe call). `Translate` is the shape
       emailReach and the drawers already type their translator with. */
    const t: Translate = _tAny;
    /* 200-glance §L — the viewer's id, narrowed once at the same seam to the
       shape `presentedIdentity` returns (`number | null`): `collabFixedCells`
       takes it as a parameter, and an `any` argument would cost the ratchet an
       unsafe call per caller; a `typeof` guard narrows without the unsafe
       assignment an annotation costs. */
    const currentUser: number | null =
      typeof _currentUserAny === "number" ? _currentUserAny : null;
    // Phase M+2b — Costs & Fees visibility:
    //   Admin: firm-wide (every case, every collaborator, every invoice)
    //   RP-of-any-case (non-admin): scoped to ONLY RP'd cases
    //   Everyone else: nav already hides this section; render null defensively.
    const __coCanSee = _appIsAdmin || !!_appPerms.viewer?.isRpOfAnyCase;
    const __coRpCaseIds = new Set(_appPerms.viewer?.myRpCaseIds || []);
    const __coRpCcIds = new Set(_appPerms.viewer?.myRpCcIds || []);
    const __coScoped = !_appIsAdmin;
    const [coStatusFilt, setCoStatusFilt] = useState<any>("all");
    const [coTypeFilt, setCoTypeFilt] = useState<any>("all");
    /* 195 §C.3b [RULED C.0] — THIS SCREEN OWNS ITS SEARCH BOX. The text used
       to arrive as App's page-level `query` (the box above every section,
       gone in §C.3e); it is this view's own state now, and the box leads the
       filter row it filters (158 §8). `__searchOK` below is untouched: it
       matches exactly what it matched. */
    const [query, setQuery] = useState<string>("");
    /* 198 §C.0 [RULED — Laurent, 2026-09-19, three times: "it's the same as
       others with different words", "when I said replace it, I mean: ditch
       this"] — THE SIX PERIOD WORDS ARE GONE (total · calyear · ytd ·
       trimester · lastmonth · lastweek, and their client-derived bound
       strings). The screen takes the shared picker as it stands: the head
       (« Toutes les dates », which is what « Total » was) and the one list —
       the nine, the two accounting-year exercises, « Libre » — through
       periodRange.ts's one boundary copy; the post carries the resolved
       window and nothing else. No union, nothing preserved. */
    const [coPeriod, setCoPeriod] = useState<PeriodValue>({ kind: "" });
    const [coCtypeFilt, setCoCtypeFilt] = useState<any>("all");
    const [coPartnerFilt, setCoPartnerFilt] = useState<any>("all");
    /* 139 §3 — the prospect axis, sixth in a row of five that already select
       WHICH MATTERS this screen totals. Typed (rule 4) although its five
       neighbours are `any`; not renamed to match them (rule 5). Default
       "both", §2.3's ruled default, so an untouched Costs screen shows the
       same book it showed before the pass. */
    const [coProspect, setCoProspect] = useState<ProspectFilterValue>("both");
    /* 199 §D — the overview opens on hours worked (its third column, the
       first figure of Laurent's twelve); `feesBilled`, the old default, is no
       longer a column. */
    const [coCollabSort, setCoCollabSort] = useState<any>({
      k: "totalMin",
      d: -1,
    });
    const [coSortBy, setCoSortBy] = useState<any>({ k: "ref", d: 1 });
    // 169 R7 — a figure with its foreign lines beneath it, in this table's own
    // whole-euro face; the code where the € stood.
    const __fcCoStack = (v: number | null | undefined, foreign: Record<string, number> | null) =>
      foreign ? (
        <>
          {__fcCo(v)}
          {Object.entries(foreign).map(([code, x]) => (
            <div key={code} style={{ fontSize: FS.s10, color: "var(--tm)", fontWeight: 400 }}>
              {new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(Math.floor(x))} {code}
            </div>
          ))}
        </>
      ) : __fcCo(v);
    /* 193 §C (Laurent, 2026-09-17: "the unmarked, unbillable, billed, invoiced,
       paid due columns should bear their usual color to be recognizable") —
       the seven recap cells read the matter lists' own tone per member
       (RECAP_MEMBERS, matterListCfg.tsx) and DÛ's red-above-zero / green-at-
       zero (dueTone), exactly as the lists and Rapports do (ReportsView's
       :907). A cell with no figure ("—") keeps the colour it had. No new
       palette: the tones are the lists' strings, read, never restated. */
    const __rcTone = (m: RecapMember, v: unknown): string | undefined => {
      if (typeof v !== "number") return undefined;
      if (m === "due") return dueTone(v);
      return RECAP_MEMBERS.find((x) => x.m === m)?.tone;
    };
    // The seven of a matter row, read once through a named shape (the rows are
    // the untyped `__allRows`; the ratchet's rule is a type at the seam).
    interface RcCells { rcUnmarked?: unknown; rcUnbillable?: unknown; rcBillable?: unknown; rcInvoiced?: unknown; rcSent?: unknown; rcPaid?: unknown; rcDue?: unknown }
    const __rcOf = (row: unknown): RcCells => (row && typeof row === "object" ? row : {});
    /* 198 §A.3 — the effective rate's three members, read through a named
       shape the same way (`__rcOf` above): the rows are the untyped
       `__allRows`, and the ratchet's rule is a type at the seam. */
    interface ErCells {
      totalPaidExVat?: number | null;
      effectiveRatePerH?: number | null;
      /* A.3a [RULED 2026-09-20] — what the numerator discarded: how many, their gross in
         the firm's currency, the other currencies per code (null when none). */
      discardedPayments?: number;
      discardedGross?: number;
      discardedForeign?: Record<string, number> | null;
    }
    const __erOf = (row: unknown): ErCells => (row && typeof row === "object" ? (row as ErCells) : {});
    /* 199 §B.4 — the line's new members (costsLine, caseFinancials.js), read
       through a named shape the same way: % costs, the total rate, B.2's
       hours numerator, invoiced, due ex-VAT with what it discarded, the
       effective margin. */
    interface LnCells {
      costsRatio?: number | null;
      totalRate?: number | null;
      partnerMin?: number | null;
      totalInvoiced?: number | null;
      totalDueExVat?: number | null;
      discardedDueDocs?: number;
      discardedDueGross?: number;
      effectiveMargin?: number | null;
    }
    const __lnOf = (row: unknown): LnCells => (row && typeof row === "object" ? row : {});
    // 198 §E.3 — the table's whole-euro face is `fcWhole` at module scope now (the
    // overheads sheet reads the same one); this name keeps its readers below.
    const __fcCo = fcWhole;
    // 199 §C — `pctCo` / `hCo` at module scope now (the activity cell reads them)
    const __pctCo = pctCo;
    const __hCo = hCo;
    const __ratCo = ratCo;
    /* 198 §C — the period's window, LOCAL dates through the one copy (the
       UTC-shifted `toISOString().slice(0, 10)` bounds that stood here are
       gone with the six words); null = every date. The two exercise kinds
       read the firm's start off `firmSettings` (D.3). */
    const __periodWindow = periodBounds(coPeriod, firmSettings);
    const __caScoped = __coScoped
      ? cases.filter((c: any) => __coRpCaseIds.has(c.id))
      : cases;
    const __ccScoped = __coScoped
      ? collectiveCases.filter((c: any) => __coRpCcIds.has(c.id))
      : collectiveCases;
    const __allCases = [
      ...__caScoped.map((c: any) => ({
        ...c,
        _kind: "regular",
        _kid: c.id,
        _kref: c.ref,
      })),
      ...__ccScoped.map((c: any) => ({
        ...c,
        _kind: "collective",
        _kid: c.id,
        _kref: c.ref,
      })),
    ];
    const __statusOK = (c: any) => {
      if (coStatusFilt === "all") return true;
      if (coStatusFilt === "active") return c.status === "active";
      if (coStatusFilt === "suspended")
        return c.status === "pending" || c.status === "suspended";
      if (coStatusFilt === "closed")
        return c.status === "closed" || c.status === "archived";
      return true;
    };
    const __typeOK = (c: any) => {
      if (coTypeFilt === "all") return true;
      if (coTypeFilt === "regular") return c._kind === "regular";
      if (coTypeFilt === "collective") return c._kind === "collective";
      return true;
    };
    const __searchOK = (c: any) => {
      const sq = (query || "").trim().toLowerCase();
      if (!sq) return true;
      const ref = (c._kref || "").toLowerCase();
      const title = (c.title || "").toLowerCase();
      // 05m step C — search what is ON SCREEN, not what is in the column. The
      // stored string is a label in the save-time language, so searching
      // "financial" missed a row displaying "Financial" merely because it was
      // saved in French. resolveTaxonomyLabel returns the raw value when
      // nothing resolves, so an orphan stays searchable by what it holds.
      const ctype = String(
        resolveTaxonomyLabel(c.ctype, caseTypeEntries) || "",
      ).toLowerCase();
      const rp = (c.responsiblePartners || [])[0]
        ? collaborators.find((x: any) => x.id === (c.responsiblePartners || [])[0])
        : null;
      const partner = rp ? pname(rp).toLowerCase() : "";
      return (
        ref.includes(sq) ||
        title.includes(sq) ||
        ctype.includes(sq) ||
        partner.includes(sq)
      );
    };
    // 05m step C — BOTH SIDES OF THIS COMPARISON ARE LABELS, and they are not
    // guaranteed to be the same label. The options at :530 are built from
    // `caseTypesList`, i.e. labels in the CURRENT language; `c.ctype` holds the
    // label in the language of whoever SAVED the row. Prod carries the pair
    // that proves it — `Financial` and `Financier`, one entry, two rows
    // (census 2026-08-03) — so a French reader's "Financier" filter dropped
    // the English-saved case, silently, in a money view. Compare the ENTRY.
    //
    // The raw-string fallback is deliberate and covers two real states: a
    // stored value the vocabulary no longer contains (which must stay findable
    // by the string it actually holds), and the pre-hydration window where
    // `caseTypeEntries` is empty and nothing resolves.
    const __wantCtype =
      coCtypeFilt === "all" ? null : resolveTaxonomyEntry(coCtypeFilt, caseTypeEntries);
    const __ctypeOK = (c: any) => {
      if (coCtypeFilt === "all") return true;
      const got = __wantCtype ? resolveTaxonomyEntry(c.ctype || "", caseTypeEntries) : null;
      if (__wantCtype && got) return __wantCtype.code === got.code;
      return (c.ctype || "") === coCtypeFilt;
    };
    const __partnerOK = (c: any) =>
      coPartnerFilt === "all"
        ? true
        : (c.responsiblePartners || []).includes(+coPartnerFilt);
    /* 139 §3 — the prospect arm, sixth beside the five above it. THIS IS WHERE
       §3.3'S RULE LANDS, and it is worth being exact about why. §3.3 says the
       toggle "reaches the server as a query parameter and the arithmetic stays
       where it is". These three tables have no query parameters: C.2 relocated
       them as a POST that CARRIES ITS OWN INPUT SET — `_postBody.matters`
       below is `__filtCases`, and `costsOverview.js` imports no prisma at all
       (financialsCompute.js:41 says so in as many words: "a pure reducer …
       the DB is not consulted"). So the only way the toggle can reach that
       server is the way its five neighbours already reach it: by choosing what
       gets posted. No aggregate the server returned is re-filtered in the
       browser, and not one line of arithmetic moved — which is the whole of
       what §3.3 protects. */
    const __prospectOK = (c: ProspectTaggable) =>
      matterPassesProspect(c, coProspect);
    const __filtCases = __allCases.filter(
      (c) =>
        __statusOK(c) &&
        __typeOK(c) &&
        __searchOK(c) &&
        __ctypeOK(c) &&
        __partnerOK(c) &&
        __prospectOK(c),
    );
    // C.2 — the three tables' money/aggregation moved server-side
    // (POST /api/financials/costs; same arithmetic, verbatim,
    // recording-verified: recordings/c2-costs/). The view posts the rows it
    // holds — the filter-selected matter subset in client order, its
    // entry/collaborator/roleRow arrays and the period pill + six
    // client-derived bound strings — and displays the returned rows and
    // insertion-order grand totals; it computes none of the money.
    // Latest-wins: a stale response never overwrites a newer one; the
    // previous result stays on screen until the next response lands (empty
    // tables before the first). The rich local partnerCo/collab objects are
    // overlaid back by id (the wire carries reduced echoes). The sort and
    // the table-1 grand-total footer (float sums over the USER-SORTED row
    // order) keep operating client-side on the server-handed rows — the
    // reports-move precedent. The request is keyed by its reduced JSON wire
    // body (__filtCases is freshly built every render — array identity
    // would re-post forever).
    const { rows: _roleRows } = useRoleLabels();
    const [costsCalc, setCostsCalc] = useState<any>(null);
    const _ccSeq = useRef(0);
    // 161 §10/§9 — the overheads SHEET, for its editor. 213 §9 [RULED 33] — the
    // sheet is read by admins and the Partner role alone: the settings answer
    // carries no `overheads` to anyone else (null here, no editor), and the costs
    // compute reads it from the DATABASE, never from this post — so a matter's
    // share and margin are the same whoever looks. A save that lands asks the
    // compute again (`ohRev`): a typed cell counts once it is stored.
    const [ohSheet, setOhSheet] = useState<Record<string, Record<string, Record<string, number>>> | null>(null);
    const [ohRev, setOhRev] = useState(0);
    useEffect(() => {
      let live = true;
      (getFirmSettings() as Promise<{ overheads?: unknown } | null>)
        .then((fs) => {
          if (!live) return;
          if (!fs || !Object.prototype.hasOwnProperty.call(fs, "overheads")) { setOhSheet(null); return; }
          const s = fs.overheads && typeof fs.overheads === "object" && !Array.isArray(fs.overheads)
            ? (fs.overheads as Record<string, Record<string, Record<string, number>>>) : {};
          setOhSheet(s);
        })
        .catch(() => { if (live) setOhSheet(null); });
      return () => { live = false; };
    }, []);
    const _postBody = {
      matters: __filtCases.map(redCostsMatter),
      entries: (timeEntries || []).map(redCostsEntry),
      collaborators: (collaborators || []).map(redCostsCo),
      roleRows: redRoleRows(_roleRows),
      matterInvoices: (invoices || []).map(redRecapInv),
      // 198 §C.0 — the resolved window travels (costsOverview.js's "window" arm:
      // from/to inclusive local dates, either side null = open); no window is "total".
      period: __periodWindow
        ? { filt: "window", from: __periodWindow[0], to: __periodWindow[1] }
        : { filt: "total" },
    };
    const _postKey = JSON.stringify(_postBody);
    useEffect(() => {
      if (!__coCanSee) return;
      const seq = ++_ccSeq.current;
      computeCostsOverview(JSON.parse(_postKey))
        .then((r: any) => {
          if (_ccSeq.current !== seq) return;
          setCostsCalc(r);
        })
        .catch(() => {
          if (_ccSeq.current === seq) setCostsCalc(null);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_postKey, __coCanSee, ohRev]);
    /* 199 §B.0 — the ref column's measured width; keyed on the served refs so a
       filter change re-measures. Called before the gate below: a hook never
       sits behind an early return. */
    const __caseTableRef = useRef<HTMLTableElement>(null);
    const __refKey = ((costsCalc as { caseRows?: { ref?: string | null }[] } | null)?.caseRows ?? []).map((r) => r.ref ?? "").join("|");
    useRefColumnWidth(__caseTableRef, __refKey);
    /* 199 §C.1 — the case × activity table shares the case table's two sticky
       columns and the same measured width. */
    const __caseActTableRef = useRef<HTMLTableElement>(null);
    useRefColumnWidth(__caseActTableRef, __refKey);
    /* 199 §C.0a — the activity columns are the `activity_code` taxonomy in its
       own order (useTaxonomy: archived out, sorted by `order`); typed once at
       the seam. */
    const __actEntries = useTaxonomy("activity_code").entries as TaxonomyEntry[];
    // 203 §A.2.2 — the two activity tables' sorts (200 §C.1's, one per mount), lifted here
    const [caseActSort, setCaseActSort] = useState<ActivitySort | null>(null);
    const [collabActSort, setCollabActSort] = useState<ActivitySort | null>(null);
    if (!__coCanSee) return null;
    const _ovCo = (red: any) =>
      red ? (collaborators.find((x: any) => x.id === red.id) ?? red) : red;
    const __allRows = (costsCalc?.caseRows || []).map((r: any) => ({
      ...r,
      partnerCo: _ovCo(r.partnerCo),
      // 161 §10 — the ten recap members FLATTENED for the flat r[key] sorter:
      // entry/document members show their ex-VAT fees+costs column (wc), the
      // rest are single figures. Same server values, one accessor shape.
      rcUnmarked: r.recap?.unmarked?.wc ?? null,
      rcUnbillable: r.recap?.unbillable?.wc ?? null,
      rcBillable: r.recap?.billable?.wc ?? null,
      rcInvoiced: r.recap?.invoiced?.wc ?? null,
      /* 190 round 9 [AV]/[AW] — ENVOYÉ is right of the bar: the documents as
         issued, VAT included (`vt`), the member the strip and the lists read;
         the four left of it stay the ex-VAT column. One screen, two columns. */
      rcSent: r.recap?.sent?.vt ?? null,
      rcPaid: r.recap?.paid ?? null,
      rcDue: r.recap?.due ?? null,
      // 169 R7 — the other currencies' paid/due, per code (null when none).
      rcPaidForeign: foreignOf(r.recap, "paid"),
      rcDueForeign: foreignOf(r.recap, "due"),
      rcStaff: r.recap?.staffCost ?? null,
      rcOverheads: r.recap?.overheads ?? null,
      rcMargin: r.recap?.grossMargin ?? null,
    }));
    const __rcTotals = costsCalc?.caseRecapTotals || null;
    /* 193 §C — the grand-total row's seven take the same tones as the rows
       (C.3 "totals row included"); staff cost and a member with no figure keep
       the row's accent. */
    const __rcTotalTone = (k: RecapMember | "staffCost"): string => {
      if (k === "staffCost") return "var(--ac)";
      const totals = __rcTotals as Record<string, unknown> | null;
      return __rcTone(k, totals ? totals[k] : null) ?? "var(--ac)";
    };
    const __sortKey = coSortBy.k;
    const __sortDir = coSortBy.d;
    const __sorted = [...__allRows].sort((a, b) => {
      const ak = (a as any)[__sortKey],
        bk = (b as any)[__sortKey];
      if (["ref", "title", "type", "status", "partner"].includes(__sortKey)) {
        return __sortDir * String(ak || "").localeCompare(String(bk || ""));
      }
      const av = ak == null ? -Infinity : ak;
      const bv = bk == null ? -Infinity : bk;
      return __sortDir * (av - bv);
    });
    /* 199 — the footer's accumulator is TYPED (the ratchet's rule: a type at
       the seam), so its readers below are typed reads. */
    interface GtAcc {
      totalMin: number; totalFeesBilled: number; totalInvoiced: number; totalPaid: number; totalCosts: number;
      totalPaidExVat: number; discardedPayments: number; discardedGross: number; discardedForeign: Record<string, number>;
      partnerMin: number; totalDueExVat: number; discardedDueDocs: number; discardedDueGross: number; effectiveMargin: number | null;
    }
    const __gt = __sorted.reduce<GtAcc>(
      (acc, r) => {
        acc.totalMin += r.totalMin;
        acc.totalFeesBilled += r.totalFeesBilled;
        acc.totalInvoiced += r.totalInvoiced;
        acc.totalPaid += r.totalPaid;
        acc.totalCosts += r.totalCosts;
        /* 198 §A.3 / A.3a [RULED — Laurent, 2026-09-20: "discard all payments
           not linked to a pro forma"] — the firm-wide numerator is Σ of the
           rows' resolved ex-VAT receipts; what a row discarded travels (the
           count, the gross, the other currencies per code) so the total's
           hover can name it. Nothing nulls the figure. */
        const er = __erOf(r);
        acc.totalPaidExVat += er.totalPaidExVat ?? 0;
        acc.discardedPayments += er.discardedPayments ?? 0;
        acc.discardedGross += er.discardedGross ?? 0;
        if (er.discardedForeign) {
          for (const [code, v] of Object.entries(er.discardedForeign)) acc.discardedForeign[code] = (acc.discardedForeign[code] ?? 0) + v;
        }
        /* 199 §B.4 — the line's new totals: B.2's hours numerator (the grand
           row's % is hours over hours), due ex-VAT with its discarded
           documents, and the effective margin — null only while EVERY row's
           is (a firm with no overheads sheet), as the recap totals do. */
        const ln = __lnOf(r);
        acc.partnerMin += ln.partnerMin ?? 0;
        acc.totalDueExVat += ln.totalDueExVat ?? 0;
        acc.discardedDueDocs += ln.discardedDueDocs ?? 0;
        acc.discardedDueGross += ln.discardedDueGross ?? 0;
        if (ln.effectiveMargin != null) acc.effectiveMargin = (acc.effectiveMargin ?? 0) + ln.effectiveMargin;
        return acc;
      },
      {
        totalMin: 0,
        totalFeesBilled: 0,
        totalInvoiced: 0,
        totalPaid: 0,
        totalCosts: 0,
        totalPaidExVat: 0,
        discardedPayments: 0,
        discardedGross: 0,
        discardedForeign: {},
        partnerMin: 0,
        totalDueExVat: 0,
        discardedDueDocs: 0,
        discardedDueGross: 0,
        effectiveMargin: null,
      },
    );
    const __gtHours = __gt.totalMin / 60;
    const __gtAvg = __gtHours > 0 ? __gt.totalFeesBilled / __gtHours : 0;
    /* 198 §A.3 — the firm-wide effective rate: the SAME denominator as
       `__gtAvg` (every hour of every matter shown), the resolved ex-VAT
       receipts above it; 0 renders "—" exactly as `__gtAvg`'s does. */
    const __gtEffective = __gtHours > 0 ? __gt.totalPaidExVat / __gtHours : 0;
    /* A.3a [RULED 2026-09-20] — THE HOVER SAYS WHAT WAS DISCARDED, and nothing
       at all when nothing was: how many payments, and their gross — the
       firm's currency in the tables' own whole-euro face, the other
       currencies per code as `__fcCoStack` prints them, never converted. A
       discarded payment leaves its hours in the denominator, so the figure
       reads low; the sentence says that too. */
    const __discardedTitle = (er: ErCells): string | undefined => {
      const count = er.discardedPayments ?? 0;
      if (!(count > 0)) return undefined;
      const parts: string[] = [];
      if ((er.discardedGross ?? 0) > 0) parts.push(fcWhole(er.discardedGross));
      for (const [code, v] of Object.entries(er.discardedForeign ?? {})) parts.push(`${INT_DE.format(Math.floor(v))} ${code}`);
      const gross = parts.length ? parts.join(" + ") : fcWhole(0);
      return t("costs.col.effective_rate_h_discarded", {
        count,
        gross,
        fallback: `${String(count)} payment(s) discarded — no pro forma with a VAT split behind them — ${gross} gross; their hours still count`,
      });
    };
    /* 199 B.2 [RULED] — « % PAR ASSOCIÉ » is HOURS: the firm-wide cell divides
       the fee-earners' minutes by every minute, as each row does. */
    const __gtPctPartner =
      __gt.totalMin > 0 ? (__gt.partnerMin / __gt.totalMin) * 100 : null;
    /* 199 B.4 (15) — the ex-VAT Dû's hover: how many sent documents store no
       VAT split and were DISCARDED (A.3a's rule, one document at a time),
       with their gross balance; nothing when none. */
    const __dueDiscardedTitle = (ln: LnCells): string | undefined => {
      const count = ln.discardedDueDocs ?? 0;
      if (count <= 0) return undefined;
      const gross = fcWhole(ln.discardedDueGross ?? 0);
      return t("costs.col.total_due_ex_vat_discarded", {
        count,
        gross,
        fallback: `${String(count)} sent document(s) discarded — no VAT split stored — ${gross} still owed on them, gross`,
      });
    };
    /* 200 §G.2 — `__gtPctInvoiced` · `__gtPctPaid` · `__gtReturn` ·
       `__gtPctReturn` STOOD HERE and are GONE: no JSX read them since 161 §10
       (the grep across src/ finds their five declarations and nothing else —
       `__retClr`, the colour they once fed, keeps its fourteen readers on the
       served figures). */
    /* 203 §A.2.1 — THE MATTER TABLE'S EXPORT, from the one definition (A.1's
       module): WHAT IS ON SCREEN — `__sorted` (the five filters, the prospect
       axis, the period window and the sort as the reader left them), the
       twenty-six columns in Laurent's order (199 §B — the `<th>` order below,
       restated here as text; the keys are the headings' own, so the words
       cannot drift, only the order could), each cell the face its `<td>`
       shows (the partner's NAME, whose badge shows initials; the title whole,
       which the cell truncates; paid/due with their other currencies on one
       line, " · " between — ReportsView's own text stack). The grand-total row
       is the table's first row; the exports carry it LAST, in the shape
       ReportsView passes: Word and print take it, Excel takes the rows alone.
       The firm's name is read once at the seam, as the viewer's id is. */
    interface ExCells {
      ref?: string | null; title?: string | null; type?: unknown; status?: unknown; partnerCo?: unknown;
      refRate?: number | null; pctByPartner?: number | null; avgRatePerH?: number | null; totalMin?: number | null;
      rcStaff?: number | null; rcOverheads?: number | null; rcMargin?: number | null;
      rcUnmarked?: number | null; rcUnbillable?: number | null; rcBillable?: number | null; rcInvoiced?: number | null;
      rcSent?: number | null; rcPaid?: number | null; rcDue?: number | null;
      rcPaidForeign?: Record<string, number> | null; rcDueForeign?: Record<string, number> | null;
    }
    const __exOf = (row: unknown): ExCells => (row && typeof row === "object" ? row : {});
    interface RcTotals {
      staffCost?: number | null; overheads?: number | null; grossMargin?: number | null;
      unmarked?: number | null; unbillable?: number | null; billable?: number | null; invoiced?: number | null;
      sent?: number | null; paid?: number | null; due?: number | null;
      foreign?: Readonly<Record<string, unknown>> | null;
    }
    const __rcT = __rcTotals as RcTotals | null;
    const __firmName = firmNameOf(firmSettings);
    // the grand-total row's recap cells: "—" while the server's totals are absent (§4.2), as the row shows
    const __rcCell = (v: number | null | undefined): string => (__rcT ? __fcCo(v) : "—");
    const __rcStack = (m: "paid" | "due"): string => (__rcT ? __fcCoText(__rcT[m], foreignOf(__rcT, m)) : "—");
    // 169 R7's stack as TEXT: the home figure, then "amount CODE" per other currency.
    const __fcCoText = (v: number | null | undefined, foreign: Record<string, number> | null | undefined): string =>
      foreign
        ? [__fcCo(v), ...Object.entries(foreign).map(([code, x]) => `${INT_DE.format(Math.floor(x))} ${code}`)].join(" · ")
        : __fcCo(v);
    const __labelOr = (code: unknown, entries: unknown): string => (code ? String(resolveTaxonomyLabel(code, entries)) : "—");
    const __coExportTitle = `${t("nav.costs")} — ${t("costs.export.matters", { fallback: "Matters" })}`;
    const __coHeaders: string[] = [
      t("costs.col.ref"), t("costs.col.title"), t("costs.col.type"), t("costs.col.partner"), t("costs.col.status"),
      t("costs.col.ref_rate"), t("costs.col.pct_costs", { fallback: "% costs" }), t("costs.col.total_rate", { fallback: "Total rate" }),
      t("costs.col.pct_by_partner"), t("costs.col.avg_rate_h"), t("costs.col.effective_rate_h", { fallback: "Paid/h." }),
      t("costs.col.total_time"), t("costs.col.total_invoiced"), t("costs.col.rc_staff_cost", { fallback: "Staff costs" }),
      t("costs.col.rc_overheads", { fallback: "Overheads" }), t("costs.col.rc_gross_margin", { fallback: "Gross margin" }),
      t("costs.col.total_paid_ex_vat", { fallback: "Paid ex VAT" }), t("costs.col.total_due_ex_vat", { fallback: "Due ex VAT" }),
      t("costs.col.effective_margin", { fallback: "Effective margin" }),
      t("costs.col.rc_unmarked", { fallback: "Unmarked" }), t("costs.col.rc_unbillable", { fallback: "Unbillable" }),
      t("costs.col.rc_billable", { fallback: "Billable" }), t("costs.col.rc_invoiced", { fallback: "Invoiced" }),
      t("cc.col.matter_sent", { fallback: "Sent" }), t("costs.col.rc_paid", { fallback: "Paid" }), t("costs.col.rc_due", { fallback: "Due" }),
    ];
    // the figures are right-aligned from the reference rate on, as the `<th>`s are
    const __coRightCols = __coHeaders.map((_, i) => i).filter((i) => i >= 5);
    const __coRow = (row: unknown): string[] => {
      const x = __exOf(row);
      const ln = __lnOf(row);
      const er = __erOf(row);
      return [
        x.ref ?? "", x.title ?? "—", __labelOr(x.type, caseTypeEntries),
        x.partnerCo ? String(pname(x.partnerCo)) : "—", __labelOr(x.status, caseStatusEntries),
        __ratCo(x.refRate), __pctCo(ln.costsRatio), __ratCo(ln.totalRate), __pctCo(x.pctByPartner), __ratCo(x.avgRatePerH),
        __ratCo(er.effectiveRatePerH), __hCo(x.totalMin), __fcCo(ln.totalInvoiced), __fcCo(x.rcStaff), __fcCo(x.rcOverheads),
        __fcCo(x.rcMargin), __fcCo(er.totalPaidExVat), __fcCo(ln.totalDueExVat), __fcCo(ln.effectiveMargin),
        __fcCo(x.rcUnmarked), __fcCo(x.rcUnbillable), __fcCo(x.rcBillable), __fcCo(x.rcInvoiced), __fcCo(x.rcSent),
        __fcCoText(x.rcPaid, x.rcPaidForeign), __fcCoText(x.rcDue, x.rcDueForeign),
      ];
    };
    const __coTotalRow: (string | null)[] = [
      `${t("costs.grand_total")} (${t("costs.cases_count", { count: __sorted.length })})`,
      null, null, null, null, "—", "—", "—",
      __pctCo(__gtPctPartner), __ratCo(__gtAvg), __ratCo(__gtEffective), __hCo(__gt.totalMin), __fcCo(__gt.totalInvoiced),
      __rcCell(__rcT?.staffCost), __rcCell(__rcT?.overheads), __rcCell(__rcT?.grossMargin),
      __fcCo(__gt.totalPaidExVat), __fcCo(__gt.totalDueExVat), __fcCo(__gt.effectiveMargin),
      __rcCell(__rcT?.unmarked), __rcCell(__rcT?.unbillable), __rcCell(__rcT?.billable), __rcCell(__rcT?.invoiced), __rcCell(__rcT?.sent),
      __rcStack("paid"), __rcStack("due"),
    ];
    // C.2's guard, Reports' own: no export of the pre-response emptiness.
    const __exportPending = costsCalc == null;
    const __coDress: ExportDress = { theme: dayTheme, font: appFont, rightCols: __coRightCols, keyCols: 2 }; // 203-glance §C · 203-glance-2 §C · 204 §D — Ref + Title open every block
    const __coExport: ExportHandlers = {
      pdf: () => { printTable(__coExportTitle, __coHeaders, __sorted.map(__coRow), __coTotalRow, __firmName, __coDress); },
      word: () => { void exportWord(__coExportTitle, __coHeaders, __sorted.map(__coRow), __firmName, __coTotalRow, __coDress); },
      excel: () => { void exportExcel(__coExportTitle, __coHeaders, __sorted.map(__coRow), __coTotalRow, __coDress); }, // §D
    };
    const __cycleSort = (k: any) =>
      setCoSortBy((p: any) => (p.k === k ? { k, d: -p.d } : { k, d: 1 }));
    const __sInd = (k: any) => <SortCaret active={coSortBy.k === k} d={coSortBy.d as number} />; // 200-glance §M.2 — the one sort caret
    const __collabRows = (costsCalc?.collabRows || []).map((r: any) => ({
      ...r,
      collab: _ovCo(r.collab),
    }));
    const __ckey = coCollabSort.k;
    const __cdir = coCollabSort.d;
    const __collabSorted = [...__collabRows].sort((a, b) => {
      if (__ckey === "name" || __ckey === "role") {
        const av = __ckey === "name" ? pname(a.collab) : a.collab.role || "";
        const bv = __ckey === "name" ? pname(b.collab) : b.collab.role || "";
        return __cdir * String(av).localeCompare(String(bv));
      }
      const av = a[__ckey] == null ? -Infinity : a[__ckey];
      const bv = b[__ckey] == null ? -Infinity : b[__ckey];
      return __cdir * (av - bv);
    });
    // Insertion-order grand totals — server-computed; the fallback is the
    // browser's own empty-world reduce shape (recording case
    // S15-empty-arrays), shown until the first response lands.
    const __cgt = costsCalc?.collabTotals ?? {
      totalMin: 0,
      feesBilled: 0,
      amtInvoiced: 0,
      amtPaid: 0,
      cost: 0,
      pctInvoiced: null,
      pctPaid: null,
      theoROI: 0,
      theoROIPct: null,
      marginPerH: null,
      realROI: 0,
      realROIPct: null,
      realMarginPerH: null,
    };
    /* 199 §D — the overview's members on their new basis, read through a named
       shape: (6) `theoROI` is invoiced − cost, (7) `marginPerH`, (8)
       `theoROIPct` over the invoiced, (9) `amtPaid` EX-VAT, (10) `realROI`,
       (11) `realMarginPerH`, (12) `realROIPct` over the paid. */
    interface OvCells {
      totalMin?: number | null; cost?: number | null; amtInvoiced?: number | null; amtPaid?: number | null;
      theoROI?: number | null; theoROIPct?: number | null; marginPerH?: number | null;
      realROI?: number | null; realROIPct?: number | null; realMarginPerH?: number | null;
    }
    const __ovOf = (row: unknown): OvCells => (row && typeof row === "object" ? row : {});
    const __cgtOv = __ovOf(__cgt);
    const __cycleCSort = (k: any) =>
      setCoCollabSort((p: any) => (p.k === k ? { k, d: -p.d } : { k, d: 1 }));
    const __sCInd = (k: any) => <SortCaret active={coCollabSort.k === k} d={coCollabSort.d as number} />; // 200-glance §M.2 — the one sort caret
    /* 199 §C — the activity tables' data, read through named shapes: each
       served row's `activity`, the two totals blocks, and the column list —
       the taxonomy's codes PLUS any code the rows carry that the taxonomy no
       longer names (rule 2, TimeEntryTable's own union: a retired code with
       hours would otherwise be counted in a row's total and shown nowhere),
       labelled by its bare code, after the taxonomy's. */
    interface AcCells { activity?: ActivityBlock | null }
    const __acOf = (row: unknown): AcCells => (row && typeof row === "object" ? row : {});
    const __actTotals = (costsCalc as { caseActivityTotals?: ActivityBlock | null; collabActivityTotals?: ActivityBlock | null } | null) ?? null;
    const __actTypes: ActivityType[] = (() => {
      const out: ActivityType[] = __actEntries
        .filter((e) => !isDividerCode(e.code))
        .map((e) => ({ code: e.code, label: String(pickEntryLabel(e)) }));
      const known = new Set(out.map((x) => x.code));
      const adopt = (blk: ActivityBlock | null | undefined) => {
        for (const code of Object.keys(blk?.byType ?? {})) {
          if (!known.has(code)) { known.add(code); out.push({ code, label: code }); }
        }
      };
      for (const r of __sorted) adopt(__acOf(r).activity);
      for (const r of __collabSorted) adopt(__acOf(r).activity);
      return out;
    })();
    const __actCellTitle = t("costs.activity.cell_title", { fallback: "Hours · what they cost the firm (each entry at the internal rate of its own date) · share of this row's hours" });
    // 199 §C — the dress is module-scope now (TH_ST · TH_ST_R · TD_R · TD_L);
    // these names keep their readers below.
    const __thSt = TH_ST;
    const __thStR = TH_ST_R;
    const __tdR = TD_R;
    /* 190 round 11 [BA] — THE BASIS BAR between FACTURÉ and ENVOYÉ, "as in the
       strip": the ENVOYÉ column's header, every row and the grand-total cell
       carry the strip's own separator (CountersStrip's `BASIS_BAR`) as their
       left border, so one line runs the table's full height — left of it the
       work (ex-VAT), right of it the cash (the documents as issued). */
    const __thStRBar: CSSProperties = { ...__thStR, borderLeft: BASIS_BAR_BORDER };
    const __tdRBar: CSSProperties = { ...__tdR, borderLeft: BASIS_BAR_BORDER };
    const __tdL = TD_L;
    /* 203 §A.2.2 — THE TWO ACTIVITY TABLES' EXPORTS, from the one definition:
       the rows are built here (they were the JSX's inline arrays) so the table
       and its export read ONE array; the export takes them in the order the
       reader left — `sortActivityRows` over the host's sort, the very function
       the table sorts with — and writes, per row, the two fixed cells as text
       and one cell per activity column: the three stacked figures on one line,
       " · " between (hours · cost · share), empty where the cell is empty, as
       on screen. The totals row (the table's first) travels last, its label
       spanning the two fixed columns as it does on screen. */
    const __caseActRows: ActivityRow[] = __sorted.map((r) => {
      const fixedSort = [String(r.ref ?? ""), String(r.title ?? "")];
      return {
        key: String(r.caseObj._kind) + ":" + String(r.caseObj._kid),
        fixed: (
          <>
            <td className="mono sticky-col-1" style={__tdL} title={r.caseObj._kind === "collective" ? t("costs.tip.collective_case") : t("costs.tip.regular_case")}>
              <span className="costs-ref">{r.ref}</span>
            </td>
            <td className="sticky-col-2" style={__tdL} title={r.title}>
              {r.titleTrunc || "—"}
            </td>
          </>
        ),
        fixedSort,
        text: [fixedSort[0], fixedSort[1] || "—"],
        activity: __acOf(r).activity ?? null,
        onClick: () => nav(r.caseObj._kind === "collective" ? "cc" : "ca", r.caseObj._kid),
      };
    });
    const __collabActRows: ActivityRow[] = __collabSorted.map((r) => {
      const name = String(pname(r.collab));
      const role = String(r.collab.role ?? "");
      return {
        key: String(r.collab.id),
        fixed: collabFixedCells(r.collab, currentUser),
        fixedSort: [name, role],
        text: [name, role ? roleLabel(role) : "—"],
        activity: __acOf(r).activity ?? null,
      };
    });
    const __caseActExport = activityExport({
      title: `${t("nav.costs")} — ${t("costs.section.by_activity", { fallback: "By activity" })}`,
      fixedLabels: [t("costs.col.ref"), t("costs.col.title")],
      totalLabel: `${t("costs.grand_total")} (${t("costs.cases_count", { count: __sorted.length })})`,
      types: __actTypes, rows: __caseActRows, sort: caseActSort, totals: __actTotals?.caseActivityTotals ?? null, firmName: __firmName, dayTheme, appFont,
      legendTitle: t("costs.activity.legend", { fallback: "Activity codes" }), // 204 §D
    });
    const __collabActExport = activityExport({
      title: `${t("nav.costs")} — ${t("costs.section.per_collaborator", { fallback: "Per collaborator" })}`,
      fixedLabels: [t("costs.cocol.name"), t("costs.cocol.role")],
      totalLabel: `${t("costs.grand_total")} (${t("costs.collaborators_count", { count: __collabSorted.length })})`,
      types: __actTypes, rows: __collabActRows, sort: collabActSort, totals: __actTotals?.collabActivityTotals ?? null, firmName: __firmName, dayTheme, appFont,
      legendTitle: t("costs.activity.legend", { fallback: "Activity codes" }), // 204 §D
    });
    /* 203 §A.2.3 — THE COLLABORATOR OVERVIEW'S EXPORT (199 §D's twelve): the
       rows are `__collabSorted` — the reader's sort (`coCollabSort`), the
       period and the matter filters already inside them (D.2) — the name and
       the role's label read off `__collabActRows` (the same two cells, once),
       the ten figures the faces their `<td>`s show; the totals row (the
       table's first) last, its label spanning name + role as on screen. */
    const __ovHeaders: string[] = [
      t("costs.cocol.name"), t("costs.cocol.role"), t("costs.cocol.hours_worked", { fallback: "Hours worked" }), t("costs.cocol.cost"),
      t("costs.cocol.amount_invoiced"), t("costs.cocol.margin", { fallback: "Margin" }), t("costs.cocol.margin_per_h", { fallback: "Margin/h" }),
      t("costs.cocol.theo_roi_pct"), t("costs.cocol.amount_paid_ex_vat", { fallback: "Paid ex VAT" }), t("costs.cocol.effective_margin", { fallback: "Effective margin" }),
      t("costs.cocol.effective_margin_per_h", { fallback: "Effective margin/h" }), t("costs.cocol.effective_roi_pct", { fallback: "% effective ROI" }),
    ];
    const __ovFigures = (ov: OvCells): string[] => [
      __hCo(ov.totalMin), __fcCo(ov.cost), __fcCo(ov.amtInvoiced), __fcCo(ov.theoROI), __ratCo(ov.marginPerH), __pctCo(ov.theoROIPct),
      __fcCo(ov.amtPaid), __fcCo(ov.realROI), __ratCo(ov.realMarginPerH), __pctCo(ov.realROIPct),
    ];
    const __ovRows = () => __collabSorted.map((r, i) => [...__collabActRows[i].text, ...__ovFigures(__ovOf(r))]);
    const __ovTotalRow: (string | null)[] = [
      `${t("costs.grand_total")} (${t("costs.collaborators_count", { count: __collabSorted.length })})`, null, ...__ovFigures(__cgtOv),
    ];
    const __ovRightCols = __ovHeaders.map((_, i) => i).filter((i) => i >= 2);
    const __ovTitle = `${t("nav.costs")} — ${t("costs.export.collaborator_overview", { fallback: "Collaborator overview" })}`;
    const __ovDress: ExportDress = { theme: dayTheme, font: appFont, rightCols: __ovRightCols, keyCols: 2 }; // 203-glance §C · 203-glance-2 §C · 204 §D — Name + Role open every block
    const __ovExport: ExportHandlers = {
      pdf: () => { printTable(__ovTitle, __ovHeaders, __ovRows(), __ovTotalRow, __firmName, __ovDress); },
      word: () => { void exportWord(__ovTitle, __ovHeaders, __ovRows(), __firmName, __ovTotalRow, __ovDress); },
      excel: () => { void exportExcel(__ovTitle, __ovHeaders, __ovRows(), __ovTotalRow, __ovDress); }, // §D
    };
    /* 198 §B — the three filter rows' dress: the row wraps, its label is the
       small uppercase word this screen already gave its filters. */
    const __filterRow: CSSProperties = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };
    /* 198-bis §B.1 [RULED — Laurent, 2026-09-20: "the title of the filters must
       stay in front of them at all times"] — A LABEL NEVER LEAVES ITS FILTER.
       MEASURED (Chromium, the running staging): a row's children were a FLAT
       sequence — label, control, label, control — every one its own flex item,
       so the wrap could break BETWEEN a label and the control it names: at
       1600 « Statut » ended row 2 while its segments began the next line, at
       1280 « Type de procédure », at 1024 both. Each label + control is now
       ONE flex item (this inline-flex wrapper), so a pair that does not fit
       moves to the next line WHOLE — 195 §E.3's rule on the Communication
       rows. The row still wraps; nothing is fixed-width, nothing re-ordered. */
    const __filterPair: CSSProperties = { display: "inline-flex", gap: 8, alignItems: "center" };
    const __filterLabel: CSSProperties = {
      fontSize: FS.s9_5,
      color: "var(--tm)",
      textTransform: "uppercase",
      letterSpacing: 0.4,
      fontWeight: 600,
    };
    /* 200 §C.2 [RULED — Laurent, 2026-09-20: "mirror the 'case…' search field
       and the period field of above the case table, so one could modify
       either in either place without needing to scroll upwards all the
       time"] — THE SAME TWO CONTROLS, MOUNTED THREE TIMES, BOUND TO THE SAME
       STATE (`query`, `coPeriod`): under the filters as before, under « Par
       activité » [JUDGEMENT — advisor: the same reason, that table is as far
       below the fold] and under « Par collaborateur ». One render helper, no
       syncing code — a second mount of one state is in step by construction. */
    const __searchAndPeriodRow = (marginBottom?: number, trailing?: ReactNode) => (
      <div style={marginBottom == null ? __filterRow : { ...__filterRow, marginBottom }}>
            {/* 195 §C.3b — the box leads the row it filters (158 §8's rule);
                198 §B.0 — relabelled « Dossier… ». `__searchOK` is untouched:
                it matches exactly what it matched. */}
            <div className="search-box" style={{ width: 240 }}>
              <Search size={12} style={{ color: "var(--tm)", flexShrink: 0 }} />
              <input
                placeholder={t("costs.filter.search_placeholder", { fallback: "Matter…" })}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div style={__filterPair}>
              <span style={__filterLabel}>{t("costs.filter.period")}</span>
              {/* 198 §C.0/C.1 — the SHARED picker, the time-entries table's own
                  shell (TimeEntryTable's `.search-box teActBox`: the paint-nothing
                  MenuSelect face over the app's menu, its ChevronDown, and the two
                  date boxes « Libre » opens beside it). C.3: the head stays a
                  separate list a host spreads or not — this screen had « Total »,
                  so it keeps the head. */}
              <div className="search-box teActBox">
                <MenuSelect
                  ariaLabel={t("costs.filter.period")}
                  title={t("costs.filter.period")}
                  value={coPeriod.kind}
                  onChange={(v) => {
                    setCoPeriod((p) => ({ kind: v as PeriodValue["kind"], from: p.from, to: p.to }));
                  }}
                  options={[...PERIOD_ALL_OPTIONS, ...PERIOD_OPTIONS].map(
                    (o) => [o.kind, t(o.key, { ...o.vars, fallback: o.fallback })] as [string, string], // 213B §4 — the quarters' `n`
                  )}
                  caret={false}
                  intrinsicWidth
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    width: "100%",
                    minWidth: 0,
                    height: "auto",
                    color: "var(--tp)",
                    fontSize: "var(--fs-11)",
                    fontFamily: "inherit",
                  }}
                />
                <ChevronDown size={12} style={{ color: "var(--tm)", flexShrink: 0 }} />
              </div>
              {coPeriod.kind === "free" && (
                <div className="search-box" style={{ width: "auto", gap: 4 }}>
                  <FreeDateInput
                    aria-label={t("te.filter.period_from", { fallback: "From" })}
                    title={t("te.filter.period_from", { fallback: "From" })}
                    value={coPeriod.from || ""}
                    style={{ width: "auto", colorScheme: "inherit" }}
                    onCommit={(v) => {
                      setCoPeriod((p) => ({ ...p, from: v }));
                    }}
                  />
                  <span style={{ color: "var(--tm)", fontSize: FS.s10 }}>→</span>
                  <FreeDateInput
                    aria-label={t("te.filter.period_to", { fallback: "To" })}
                    title={t("te.filter.period_to", { fallback: "To" })}
                    value={coPeriod.to || ""}
                    style={{ width: "auto", colorScheme: "inherit" }}
                    onCommit={(v) => {
                      setCoPeriod((p) => ({ ...p, to: v }));
                    }}
                  />
                </div>
              )}
            </div>
            {/* 203 §A.2.1 — the matter table's export trio rides the row that
                heads it (the table has had no heading since 196 §C.1); the
                other mounts pass nothing here and carry theirs in their h3. */}
            {trailing}
          </div>
    );
    const __retClr = (v: any) =>
      v == null ? "var(--tm)" : v >= 0 ? "var(--gn)" : "var(--rd)";
    return (
      <div>
        {__coScoped && (
          <div
            style={{
              marginBottom: 10,
              padding: "6px 10px",
              fontSize: FS.s11,
              color: "var(--ac)",
              background: "rgba(74,124,255,.08)",
              border: "1px solid rgba(74,124,255,.25)",
              borderRadius: RAD.r6,
            }}
          >
            {t("costs.scoped_notice")}
          </div>
        )}
        {/* 198 §B.0 [RULED — Laurent] — THE FILTERS ARE TOGGLE PAIRS, IN THREE
            ROWS: (1) Associé · (2) Type de dossier · Prospect · Type de
            procédure · Statut · (3) « Dossier… » and Période (§C). The control
            is SegmentedControl — the idiom both drawers use (194 C.3): a lit
            segment is the chosen value, NONE LIT is "all", and the lit one
            pressed again goes out. 198-bis §B.2 [RULED — Laurent, 2026-09-20:
            "the seam of the toggles is too contrasting, clutters the GUI"]:
            the drawers' outline (`DRAWER_TOGGLE_STYLE` — `--tm`, the muted
            TEXT ink, made so ONE pair reads on a busy drawer row) is NOT worn
            here: five groups over three rows made five dark rings. Each group
            wears SegmentedControl's own `--br` edge, the softer one; the two
            drawers keep theirs (194D D.5 stands). The inner seams (`--br`
            between segments) are MEASURED, not changed — Laurent rules
            whether they go. The five filter states, their "all" values and every
            predicate below (`__statusOK` … `__partnerOK`) are unchanged: only
            the buttons that set them are. The « Vue d'ensemble » heading is
            already gone (196 §C.1 — it was App.tsx's). */}
        <div style={{ marginBottom: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={__filterRow}>
            <div style={__filterPair}>
              <span style={__filterLabel}>{t("costs.filter.partner")}</span>
              <SegmentedControl
                value={coPartnerFilt}
                options={collaborators
                  .filter((c: any) => isFeeEarnerCollab(c))
                  .map((c: any) => [String(c.id), pname(c)] as const)}
                onChange={(k: string) => {
                  setCoPartnerFilt((p: any) => (p === k ? "all" : k));
                }}
              />
            </div>
          </div>
          <div style={__filterRow}>
            <div style={__filterPair}>
              <span style={__filterLabel}>{t("costs.filter.matter_kind", { fallback: "Matter type" })}</span>
              <SegmentedControl
                value={coTypeFilt}
                options={[
                  ["regular", t("costs.type.regular_cases")],
                  ["collective", t("costs.type.collective_cases")],
                ]}
                onChange={(k: string) => {
                  setCoTypeFilt((p: any) => (p === k ? "all" : k));
                }}
              />
            </div>
            <div style={__filterPair}>
              <span style={__filterLabel}>{t("matters.prospect.badge", { fallback: "Prospect" })}</span>
              {/* 139 §3's axis, its three states unchanged: the two named
                  segments, none lit = "both" (§2.3's ruled default). */}
              <SegmentedControl
                value={coProspect}
                options={prospectFilterOptions(t).filter(([k]) => k !== "both")}
                onChange={(k: string) => {
                  setCoProspect((p) => (p === k ? "both" : (k as ProspectFilterValue)));
                }}
              />
            </div>
            <div style={__filterPair}>
              <span style={__filterLabel}>{t("costs.filter.procedure_type", { fallback: "Procedure type" })}</span>
              {/* 05n — the value is the CODE; `__ctypeOK` compares through
                  resolveTaxonomyEntry, so a code matches as readily as a label. */}
              <SegmentedControl
                value={coCtypeFilt}
                options={caseTypesList.map(([v, l]: any) => [String(v), String(l ?? "")] as const)}
                onChange={(k: string) => {
                  setCoCtypeFilt((p: any) => (p === k ? "all" : k));
                }}
              />
            </div>
            <div style={__filterPair}>
              <span style={__filterLabel}>{t("costs.filter.status")}</span>
              <SegmentedControl
                value={coStatusFilt}
                options={[
                  ["active", t("costs.status.active")],
                  ["suspended", t("costs.status.suspended")],
                  ["closed", t("costs.status.closed")],
                ]}
                onChange={(k: string) => {
                  setCoStatusFilt((p: any) => (p === k ? "all" : k));
                }}
              />
            </div>
          </div>
          {__searchAndPeriodRow(undefined, <ExportButtons t={t} on={__coExport} pending={__exportPending} />)}
        </div>
        {/* 199 §B [RULED — Laurent, his list, 2026-09-20] — THE CASE TABLE'S
            COLUMNS IN HIS ORDER. The fixed part (B.0): case number · case
            title, the ref column as wide as the widest ref. The scrolling
            part (B.1): type · RP · status · hourly rate · % costs (5, NEW) ·
            total rate (6, NEW: ref × (1 + % costs / 100)) · % hours by RP (7 —
            B.2, the basis changed, the name and the column kept) · average
            rate · Payé/h. (8b — the advisor's judgement: the pair of 8, ruled
            into being the same day; Laurent overrules by saying so) · total
            hours · total invoiced ex-VAT (10 — `totalInvoiced`, the invoiced
            entries at their rates PLUS the costs surcharge: B.3 [RULED — "keep,
            all unmarked/unbillable/billable/invoiced include the costs"]) ·
            collaborator costs · general costs · gross margin · total paid ex-VAT
            (14) · total due ex-VAT (15, NEW) · effective margin (16, NEW: 14 −
            11 − 12) · unmarked · unbillable · billable · invoiced · Sent · paid
            · due (17–23, as they stood, the basis bar before Sent — 190 round
            11 [BA]). Every figure is the server's (costsLine / recapBlock);
            the sort and the grand-total footer stay client-side (C.2's
            precedent). */}
        <div className="table-wrap" style={{ overflowX: "auto" }}>
          <Table
            ref={__caseTableRef}
            className="costs-table"
            style={{ minWidth: 0, fontSize: FS.s10, borderCollapse: "collapse" }}
          >
            <thead>
              <tr>
                <th
                  className="sticky-col-1"
                  style={__thSt}
                  onClick={() => __cycleSort("ref")}
                >
                  {t("costs.col.ref")}
                  {__sInd("ref")}
                </th>
                <th
                  className="sticky-col-2"
                  style={__thSt}
                  onClick={() => __cycleSort("title")}
                >
                  {t("costs.col.title")}
                  {__sInd("title")}
                </th>
                <th style={__thSt} onClick={() => __cycleSort("type")}>
                  {t("costs.col.type")}
                  {__sInd("type")}
                </th>
                <th style={__thSt} onClick={() => __cycleSort("partner")}>
                  {t("costs.col.partner")}
                  {__sInd("partner")}
                </th>
                <th style={__thSt} onClick={() => __cycleSort("status")}>
                  {t("costs.col.status")}
                  {__sInd("status")}
                </th>
                <th style={__thStR} onClick={() => __cycleSort("refRate")}>
                  {t("costs.col.ref_rate")}
                  {__sInd("refRate")}
                </th>
                <th style={__thStR} onClick={() => { __cycleSort("costsRatio"); }}>
                  {t("costs.col.pct_costs", { fallback: "% costs" })}
                  {__sInd("costsRatio")}
                </th>
                <th style={__thStR} onClick={() => { __cycleSort("totalRate"); }} title={t("costs.col.total_rate_title", { fallback: "Reference rate × (1 + % costs / 100)" })}>
                  {t("costs.col.total_rate", { fallback: "Total rate" })}
                  {__sInd("totalRate")}
                </th>
                {/* 199 B.2 [RULED — "% hours by RP"] — the same heading, the
                    same column, a NEW MEANING: the fee-earners' minutes over
                    every minute worked (it read their share of the surcharged
                    fees until 199). The hover says what it divides. */}
                <th style={__thStR} onClick={() => { __cycleSort("pctByPartner"); }} title={t("costs.col.pct_by_partner_title", { fallback: "Minutes worked by the fee-earning roles ÷ every minute worked on the matter" })}>
                  {t("costs.col.pct_by_partner")}
                  {__sInd("pctByPartner")}
                </th>
                <th style={__thStR} onClick={() => __cycleSort("avgRatePerH")}>
                  {t("costs.col.avg_rate_h")}
                  {__sInd("avgRatePerH")}
                </th>
                {/* 198 §A.3 / 198-bis §A — « PAYÉ/H. »: the same denominator as
                    « TAUX MOYEN/H », what the clients actually paid ex-VAT above
                    it (costsOverview.js `effectiveRatePerH`); the hover says
                    what it divides, the cell's hover what it discarded. */}
                <th
                  style={__thStR}
                  onClick={() => {
                    __cycleSort("effectiveRatePerH");
                  }}
                  title={t("costs.col.effective_rate_h_title", {
                    fallback: "Payments received ex-VAT ÷ every hour worked, all marks (unmarked, unbillable, billable, invoiced)",
                  })}
                >
                  {t("costs.col.effective_rate_h", { fallback: "Paid/h." })}
                  {__sInd("effectiveRatePerH")}
                </th>
                <th style={__thStR} onClick={() => __cycleSort("totalMin")}>
                  {t("costs.col.total_time")}
                  {__sInd("totalMin")}
                </th>
                {/* 199 B.1 (10) — `totalInvoiced` back as a column (161 §10 had
                    replaced it by the recap's FACTURÉ); B.3 — the surcharge
                    stays in, the hover says so. */}
                <th style={__thStR} onClick={() => { __cycleSort("totalInvoiced"); }} title={t("costs.col.total_invoiced_title", { fallback: "The invoiced entries at their rates, plus the costs surcharge, ex VAT" })}>
                  {t("costs.col.total_invoiced")}
                  {__sInd("totalInvoiced")}
                </th>
                <th style={__thStR} onClick={() => __cycleSort("rcStaff")}>
                  {t("costs.col.rc_staff_cost", { fallback: "Staff costs" })}
                  {__sInd("rcStaff")}
                </th>
                <th style={__thStR} onClick={() => __cycleSort("rcOverheads")}>
                  {t("costs.col.rc_overheads", { fallback: "Overheads" })}
                  {__sInd("rcOverheads")}
                </th>
                <th style={__thStR} onClick={() => __cycleSort("rcMargin")}>
                  {t("costs.col.rc_gross_margin", { fallback: "Gross margin" })}
                  {__sInd("rcMargin")}
                </th>
                {/* 199 B.1 (14)–(16) — the cash side ex-VAT: what was paid
                    (198 §A.3's numerator, a column now), what the sent
                    documents still owe at their own VAT split, and the
                    effective margin (14 − 11 − 12). */}
                <th style={__thStR} onClick={() => { __cycleSort("totalPaidExVat"); }}>
                  {t("costs.col.total_paid_ex_vat", { fallback: "Paid ex VAT" })}
                  {__sInd("totalPaidExVat")}
                </th>
                <th style={__thStR} onClick={() => { __cycleSort("totalDueExVat"); }} title={t("costs.col.total_due_ex_vat_title", { fallback: "What the sent documents still owe, each at its own VAT split" })}>
                  {t("costs.col.total_due_ex_vat", { fallback: "Due ex VAT" })}
                  {__sInd("totalDueExVat")}
                </th>
                <th style={__thStR} onClick={() => { __cycleSort("effectiveMargin"); }} title={t("costs.col.effective_margin_title", { fallback: "Paid ex VAT − staff costs − overheads" })}>
                  {t("costs.col.effective_margin", { fallback: "Effective margin" })}
                  {__sInd("effectiveMargin")}
                </th>
                {/* 161 §10 [R10] — the recap's seven, in the strip's order;
                    the four left of the bar ex-VAT, the three right of it the
                    documents as issued (190 round 9 [AV]/[AW]). */}
                <th style={__thStR} onClick={() => __cycleSort("rcUnmarked")}>
                  {t("costs.col.rc_unmarked", { fallback: "Unmarked" })}
                  {__sInd("rcUnmarked")}
                </th>
                <th style={__thStR} onClick={() => __cycleSort("rcUnbillable")}>
                  {t("costs.col.rc_unbillable", { fallback: "Unbillable" })}
                  {__sInd("rcUnbillable")}
                </th>
                <th style={__thStR} onClick={() => __cycleSort("rcBillable")}>
                  {t("costs.col.rc_billable", { fallback: "Billable" })}
                  {__sInd("rcBillable")}
                </th>
                <th style={__thStR} onClick={() => __cycleSort("rcInvoiced")}>
                  {t("costs.col.rc_invoiced", { fallback: "Invoiced" })}
                  {__sInd("rcInvoiced")}
                </th>
                <th style={__thStRBar} onClick={() => __cycleSort("rcSent")}>
                  {/* [AV] — one label for one member (`costs.col.rc_sent` retired);
                      190 round 11 [AY] (reverses round 10's [AX]) — a BARE "Envoyé",
                      the lists' own word: no column header carries the VAT mention,
                      neither the strip's one line (which measured 111 px here over
                      cells needing 35) nor round 10's second line. The boundary is
                      the strip's card's to tell — "people will learn when reading
                      the strip" — and the bar's ([BA]). */}
                  {t("cc.col.matter_sent", { fallback: "Sent" })}
                  {__sInd("rcSent")}
                </th>
                {/* 161A §4 [R14] — paid/due are invoice-level "on the
                    invoices concerned" (the period's entries' documents).
                    161B §6 — THE SUB-LABEL IS A HOVER, NOT A SECOND LINE. As
                    a block `<span>` it set these two columns' minimum width
                    to a five-word sentence, so PAID and DUE were far wider
                    than the eight beside them and the table paid for it
                    everywhere. The `title=` was ALREADY on both `<th>`s —
                    measured, not invented, and it is the shape this file
                    already uses (SharedUI exports no tooltip) — so removing
                    the span loses no information and needs no third
                    mechanism. The Invoices strip's own cards keep their
                    sub-label: they have the room. */}
                <th style={__thStR} onClick={() => __cycleSort("rcPaid")} title={t("invoice.summary.invoice_level", { fallback: "on the invoices concerned" })}>
                  {t("costs.col.rc_paid", { fallback: "Paid" })}
                  {__sInd("rcPaid")}
                </th>
                <th style={__thStR} onClick={() => __cycleSort("rcDue")} title={t("invoice.summary.invoice_level", { fallback: "on the invoices concerned" })}>
                  {t("costs.col.rc_due", { fallback: "Due" })}
                  {__sInd("rcDue")}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                className="costs-table-total"
                /* 195B §B [RULED — Laurent, 2026-09-19] — the ground is the
                   class rule's `var(--as)` (App.css), no longer an inline
                   literal: inline beat the row's hover. */
                style={{
                  borderBottom: "2px solid var(--ac)",
                  fontWeight: 700,
                }}
              >
                {/* 199 §B.0 — the label spans BOTH fixed columns (one sticky
                    cell): the ref column holds nothing but refs, so its
                    measured width is the widest ref's. */}
                <td
                  className="sticky-col-12"
                  colSpan={2}
                  style={{
                    padding: "4px 6px",
                    color: "var(--ac)",
                    fontSize: FS.s10,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    fontWeight: 700,
                  }}
                >
                  {t("costs.grand_total")}{" "}
                  <span style={{ fontWeight: 400, opacity: 0.85, textTransform: "none" }}>
                    ({t("costs.cases_count", { count: __sorted.length })})
                  </span>
                </td>
                <td colSpan={3} style={{ padding: "4px 6px" }}></td>
                <td className="mono" style={{ ...__tdR, color: "var(--ac)" }}>
                  —
                </td>
                <td className="mono" style={{ ...__tdR, color: "var(--ac)" }}>
                  —
                </td>
                <td className="mono" style={{ ...__tdR, color: "var(--ac)" }}>
                  —
                </td>
                <td className="mono" style={{ ...__tdR, color: "var(--ac)" }}>
                  {__pctCo(__gtPctPartner)}
                </td>
                <td className="mono" style={{ ...__tdR, color: "var(--ac)" }}>
                  {__ratCo(__gtAvg)}
                </td>
                <td className="mono" style={{ ...__tdR, color: "var(--ac)" }} title={__discardedTitle(__gt)}>
                  {__ratCo(__gtEffective)}
                </td>
                <td className="mono" style={{ ...__tdR, color: "var(--ac)" }}>
                  {__hCo(__gt.totalMin)}
                </td>
                <td className="mono" style={{ ...__tdR, color: "var(--ac)" }}>
                  {__fcCo(__gt.totalInvoiced)}
                </td>
                <td className="mono" style={{ ...__tdR, color: "var(--ac)" }}>
                  {__rcTotals ? __fcCo(__rcTotals.staffCost) : "—"}
                </td>
                <td className="mono" style={{ ...__tdR, color: "var(--ac)" }}>
                  {__rcTotals && __rcTotals.overheads != null ? __fcCo(__rcTotals.overheads) : "—"}
                </td>
                <td
                  className="mono"
                  style={{ ...__tdR, color: __retClr(__rcTotals?.grossMargin ?? 0) }}
                >
                  {__rcTotals && __rcTotals.grossMargin != null ? __fcCo(__rcTotals.grossMargin) : "—"}
                </td>
                <td className="mono" style={{ ...__tdR, color: "var(--ac)" }} title={__discardedTitle(__gt)}>
                  {__fcCo(__gt.totalPaidExVat)}
                </td>
                <td className="mono" style={{ ...__tdR, color: "var(--ac)" }} title={__dueDiscardedTitle(__gt)}>
                  {__fcCo(__gt.totalDueExVat)}
                </td>
                <td
                  className="mono"
                  style={{ ...__tdR, color: __retClr(__gt.effectiveMargin) }}
                >
                  {__gt.effectiveMargin != null ? __fcCo(__gt.effectiveMargin) : "—"}
                </td>
                {/* 161 §10 — the server's insertion-order grand totals for
                    the seven (caseRecapTotals); "—" while absent (§4.2). */}
                {(["unmarked", "unbillable", "billable", "invoiced", "sent", "paid", "due"] as const).map((k) => (
                  <td key={k} className="mono" style={{ ...(k === "sent" ? __tdRBar : __tdR), color: __rcTotalTone(k) }}>
                    {__rcTotals
                      ? (k === "paid" || k === "due"
                        ? __fcCoStack(__rcTotals[k], foreignOf(__rcTotals, k))
                        : __fcCo(__rcTotals[k]))
                      : "—"}
                  </td>
                ))}
              </tr>
              {__sorted.map((r) => (
                <tr
                  key={r.caseObj._kind + ":" + r.caseObj._kid}
                  onClick={() =>
                    nav(
                      r.caseObj._kind === "collective" ? "cc" : "ca",
                      r.caseObj._kid,
                    )
                  }
                  style={{ cursor: "pointer" }}
                >
                  <td
                    className="mono sticky-col-1"
                    style={__tdL}
                    title={
                      r.caseObj._kind === "collective"
                        ? t("costs.tip.collective_case")
                        : t("costs.tip.regular_case")
                    }
                  >
                    {/* 199 §B.0 — the span the width hook measures */}
                    <span className="costs-ref">{r.ref}</span>
                  </td>
                  <td className="sticky-col-2" style={__tdL} title={r.title}>
                    {r.titleTrunc || "—"}
                  </td>
                  <td style={__tdL}>
                    {r.type ? (
                      <span
                        className="tag"
                        style={{
                          fontSize: FS.s9,
                          ...(ctc(r.type, caseTypeEntries) || {}),
                        }}
                      >
                        {resolveTaxonomyLabel(r.type, caseTypeEntries)}
                      </span>
                    ) : (
                      <span style={{ color: "var(--tm)" }}>—</span>
                    )}
                  </td>
                  <td style={__tdL}>
                    {r.partnerIni ? (
                      /* Phase 31 (C5) — shared photo-capable badge. 196 §C.2
                         [RULED — Laurent, 2026-09-19: the badges "are wrong
                         on this screen and must read as they do in the
                         Dossiers list"] — THE PROPS ARE THE LIST'S RP
                         COLUMN'S (matterListCfg.tsx:523): the "financial grey
                         palette" `style` (a raw rgba fill, text and border
                         that overrode the badge's own `--cb`/`--bf` and
                         framed a photo grey) is GONE, `isMe` — the viewer's
                         `--ac` ring — is passed as the list passes it, and
                         the `title` the list does not pass goes too (the
                         badge's own hover, the full name, applies). `noDim`
                         STAYS: 75 §3 rules costs an invoicing surface where
                         an archived partner renders normally — the one prop
                         the list does not pass, kept by ruling. The `{...({}
                         as any)}` no-op spread (187's census item 8) is
                         deleted with the fix, as that item prescribes. */
                      <InitialsBadge
                        noDim /* 75 §3 — costs is an invoicing surface: archived renders normally */
                        person={r.partnerCo}
                        isMe={r.partnerCo?.id === currentUser}
                        size={22}
                        fontSize={9}
                      />
                    ) : (
                      <span style={{ color: "var(--tm)" }}>—</span>
                    )}
                  </td>
                  <td style={__tdL}>
                    {r.status ? (
                      <span
                        className="tag"
                        style={{
                          fontSize: FS.s9,
                          background:
                            r.status === "active"
                              ? "rgba(52,211,153,.15)"
                              : r.status === "pending"
                                ? "rgba(251,191,36,.15)"
                                : r.status === "closed"
                                  ? "rgba(248,113,113,.15)"
                                  : "rgba(140,140,140,.15)",
                          color:
                            r.status === "active"
                              ? "var(--gn)"
                              : r.status === "pending"
                                ? "var(--am)"
                                : r.status === "closed"
                                  ? "var(--rd)"
                                  : "var(--tm)",
                        }}
                      >
                        {resolveTaxonomyLabel(r.status, caseStatusEntries)}
                      </span>
                    ) : (
                      <span style={{ color: "var(--tm)" }}>—</span>
                    )}
                  </td>
                  <td className="mono" style={__tdR}>
                    {__ratCo(r.refRate)}
                  </td>
                  <td className="mono" style={__tdR}>
                    {__pctCo(__lnOf(r).costsRatio)}
                  </td>
                  <td className="mono" style={__tdR}>
                    {__ratCo(__lnOf(r).totalRate)}
                  </td>
                  <td className="mono" style={__tdR}>
                    {__pctCo(r.pctByPartner)}
                  </td>
                  <td className="mono" style={__tdR}>
                    {__ratCo(r.avgRatePerH)}
                  </td>
                  <td className="mono" style={__tdR} title={__discardedTitle(__erOf(r))}>
                    {__ratCo(__erOf(r).effectiveRatePerH)}
                  </td>
                  <td className="mono" style={__tdR}>
                    {__hCo(r.totalMin)}
                  </td>
                  <td className="mono" style={__tdR}>
                    {__fcCo(__lnOf(r).totalInvoiced)}
                  </td>
                  <td className="mono" style={__tdR}>
                    {__fcCo(r.rcStaff)}
                  </td>
                  <td className="mono" style={__tdR}>
                    {r.rcOverheads != null ? __fcCo(r.rcOverheads) : "—"}
                  </td>
                  <td
                    className="mono"
                    style={{
                      ...__tdR,
                      color: __retClr(r.rcMargin),
                      fontWeight: 600,
                    }}
                  >
                    {r.rcMargin != null ? __fcCo(r.rcMargin) : "—"}
                  </td>
                  <td className="mono" style={__tdR} title={__discardedTitle(__erOf(r))}>
                    {__fcCo(__erOf(r).totalPaidExVat)}
                  </td>
                  <td className="mono" style={__tdR} title={__dueDiscardedTitle(__lnOf(r))}>
                    {__fcCo(__lnOf(r).totalDueExVat)}
                  </td>
                  <td
                    className="mono"
                    style={{
                      ...__tdR,
                      color: __retClr(__lnOf(r).effectiveMargin),
                      fontWeight: 600,
                    }}
                  >
                    {__lnOf(r).effectiveMargin != null ? __fcCo(__lnOf(r).effectiveMargin) : "—"}
                  </td>
                  {/* 161B §6 — ONE RULE FOR THE SEVEN: a COMPUTED value renders
                      as money, zero included; only `null` — not computable —
                      renders "—". These are always computable (the recap
                      block sums rows and clamps `due` at 0), so the
                      `x > 0 ? x : null` that stood here turned a real,
                      measured zero into "absent". DUE read "—" on SMK-01
                      while the grand total below read "0 €" and the strip
                      read "€0.00" — three renderings of the same figure, and
                      only this one lying about it.
                      `__fcCo(null)` is already "—" (:226), so dropping the
                      guard serves BOTH arms and makes each cell the same
                      expression the grand-total row uses. Overheads and
                      gross margin keep their explicit null checks above:
                      those two ARE genuinely absent when the firm has typed
                      no monthly sheet (recapBlock's `oh == null`). */}
                  <td className="mono" style={{ ...__tdR, color: __rcTone("unmarked", __rcOf(r).rcUnmarked) }}>
                    {__fcCo(r.rcUnmarked)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: __rcTone("unbillable", __rcOf(r).rcUnbillable) }}>
                    {__fcCo(r.rcUnbillable)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: __rcTone("billable", __rcOf(r).rcBillable) }}>
                    {__fcCo(r.rcBillable)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: __rcTone("invoiced", __rcOf(r).rcInvoiced) }}>
                    {__fcCo(r.rcInvoiced)}
                  </td>
                  <td className="mono" style={{ ...__tdRBar, color: __rcTone("sent", __rcOf(r).rcSent) }}>
                    {__fcCo(r.rcSent)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: __rcTone("paid", __rcOf(r).rcPaid) }}>
                    {__fcCoStack(r.rcPaid, r.rcPaidForeign)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: __rcTone("due", __rcOf(r).rcDue) }}>
                    {__fcCoStack(r.rcDue, r.rcDueForeign)}
                  </td>
                </tr>
              ))}
              {__sorted.length === 0 && (
                <tr>
                  <td
                    /* 199 §B — the empty row spans the table's REAL column
                       count: the two fixed + the 24 scrolling columns of
                       Laurent's list. */
                    colSpan={26}
                    style={{
                      textAlign: "center",
                      padding: "30px 20px",
                      color: "var(--tm)",
                      fontStyle: "italic",
                    }}
                  >
                    {t("costs.no_cases_match")}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        {/* 199 §C.1 [RULED] — « PAR ACTIVITÉ »: the rows are the case table's
            matters (its filters, its sort, its two fixed columns and their
            measured width), one column per activity type, the totals row
            first as every table here. */}
        <div style={{ marginTop: 18 }}>
          <h3 style={SECTION_H3}>
            {t("costs.section.by_activity", { fallback: "By activity" })}
            <ExportButtons t={t} on={__caseActExport} pending={__exportPending} />
          </h3>
          {__searchAndPeriodRow(8)}
          <ActivityTable
            tableRef={__caseActTableRef}
            types={__actTypes}
            cellTitle={__actCellTitle}
            sort={caseActSort}
            onSort={setCaseActSort}
            fixedHeads={[
              { key: "ref", label: t("costs.col.ref"), className: "sticky-col-1" },
              { key: "title", label: t("costs.col.title"), className: "sticky-col-2" },
            ]}
            totalHead={
              <td className="sticky-col-12" colSpan={2} style={{ padding: "4px 6px", color: "var(--ac)", fontSize: FS.s10, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>
                {t("costs.grand_total")}{" "}
                <span style={{ fontWeight: 400, opacity: 0.85, textTransform: "none" }}>
                  ({t("costs.cases_count", { count: __sorted.length })})
                </span>
              </td>
            }
            rows={__caseActRows}
            totals={__actTotals?.caseActivityTotals ?? null}
            emptyText={t("costs.no_cases_match")}
          />
        </div>
        {/* 199 §E.0 [RULED] — « PAR COLLABORATEUR » heads BOTH collaborator
            tables (§C.2's, then §D's overview): a NEW key (the seed only
            inserts; `costs.section.collaborator_overview` loses its reader,
            rows kept). §E.1 [RULED — Laurent: "general costs go on bottom"]:
            the order is the case table · « Par activité » · « Par
            collaborateur » · « Frais généraux » LAST — the sheet's mount moved
            below, its gate and its lifted state exactly as they were. */}
        <div style={{ marginTop: 18 }}>
          <h3 style={SECTION_H3}>
            {t("costs.section.per_collaborator", { fallback: "Per collaborator" })}
            <ExportButtons t={t} on={__collabActExport} pending={__exportPending} />
          </h3>
          {__searchAndPeriodRow(8)}
          {/* 199 §C.2 [RULED] — collaborators × activities, FIRST under this
              heading: the overview's rows in the overview's order, its two
              fixed cells, one column per activity type, a totals row. */}
          <ActivityTable
            types={__actTypes}
            cellTitle={__actCellTitle}
            sort={collabActSort}
            onSort={setCollabActSort}
            fixedHeads={[
              { key: "name", label: t("costs.cocol.name"), className: "sticky-col-name" },
              { key: "role", label: t("costs.cocol.role") },
            ]}
            totalHead={
              /* 200 §G.1 — the label SPANS the two fixed columns (199 B.0's
                 treatment, `sticky-col-12`): as one nowrap cell in the 145-px
                 name column it FORCED that column to 207 px (measured — the
                 199 [METHOD] (a) mechanism, one table over). */
              <td className="sticky-col-12" colSpan={2} style={{ padding: "4px 6px", color: "var(--ac)", fontSize: FS.s10, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>
                {t("costs.grand_total")}{" "}
                <span style={{ fontWeight: 400, opacity: 0.7, textTransform: "none" }}>
                  ({t("costs.collaborators_count", { count: __collabSorted.length })})
                </span>
              </td>
            }
            rows={__collabActRows}
            totals={__actTotals?.collabActivityTotals ?? null}
            emptyText={t("costs.no_collaborator_activity")}
          />
          {/* 199 §D.0 [RULED — Laurent's twelve, with his answer on what is
              dropped] — THE COLLABORATORS OVERVIEW: name · role (KEPT, second —
              "drop the unnamed EXCEPT role which should come after name as
              now") · hours worked (all four marks — the figure was right, the
              label « heures facturées » was wrong) · amount paid to them
              (`cost`, §A's rate history) · invoiced ex VAT (their hours in the
              matters, the surcharge inside — B.3) · margin for the firm (6 =
              5 − 4, BASIS CHANGED from feesBilled − cost) · margin per hour
              (7, NEW) · % theoretical ROI (8, over the invoiced) · paid in the
              matters ex VAT (9 — `amtPaid` pro-rates the matter's payments
              EX-VAT now, through the one share function) · effective margin
              (10 = 9 − 4) · effective margin per hour (11, NEW) · % effective
              ROI (12, over the paid). DROPPED: % of total fees · % invoiced ·
              % paid · % of total cost (their keys readerless, rows kept).
              D.1: sortable on every column, the totals row kept and widened.
              D.2 VERIFIED, not re-filtered: `__collabMap` is built from
              `__filtCases` under `__inPeriod` on the server, so RP and
              Période already reach this table — no second filter row. */}
          {/* 203 §A.2.3 [JUDGEMENT — advisor, overrulable] — the overview has no
              heading of its own (199 §E.0: « Par collaborateur » heads both
              collaborator tables), so its trio rides a slim line of its own,
              right-aligned, the table's marginTop becoming this line's; no
              word on the line — a heading here would be a new section name,
              which is Laurent's to give. */}
          <div style={{ display: "flex", alignItems: "center", marginTop: 12 }}>
            <ExportButtons t={t} on={__ovExport} pending={__exportPending} />
          </div>
          <div className="table-wrap" style={{ overflowX: "auto", marginTop: 4 }}>
            <Table
              className="costs-table"
              style={{ minWidth: 0, fontSize: FS.s10, borderCollapse: "collapse" }}
            >
              <thead>
                <tr>
                  <th
                    className="sticky-col-name"
                    style={__thSt}
                    onClick={() => { __cycleCSort("name"); }}
                  >
                    {t("costs.cocol.name")}
                    {__sCInd("name")}
                  </th>
                  <th style={__thSt} onClick={() => { __cycleCSort("role"); }}>
                    {t("costs.cocol.role")}
                    {__sCInd("role")}
                  </th>
                  <th style={__thStR} onClick={() => { __cycleCSort("totalMin"); }} title={t("costs.cocol.hours_worked_title", { fallback: "Every hour worked in the filtered matters and period — unmarked, unbillable, billable, invoiced" })}>
                    {t("costs.cocol.hours_worked", { fallback: "Hours worked" })}
                    {__sCInd("totalMin")}
                  </th>
                  <th style={__thStR} onClick={() => { __cycleCSort("cost"); }} title={t("costs.cocol.cost_title", { fallback: "Hours × the internal rate in force on each entry's date" })}>
                    {t("costs.cocol.cost")}
                    {__sCInd("cost")}
                  </th>
                  <th style={__thStR} onClick={() => { __cycleCSort("amtInvoiced"); }} title={t("costs.cocol.amount_invoiced_title", { fallback: "This collaborator's invoiced hours at their rates, plus the costs surcharge, ex VAT" })}>
                    {t("costs.cocol.amount_invoiced")}
                    {__sCInd("amtInvoiced")}
                  </th>
                  <th style={__thStR} onClick={() => { __cycleCSort("theoROI"); }} title={t("costs.cocol.margin_title", { fallback: "Invoiced ex VAT − amount paid to the collaborator" })}>
                    {t("costs.cocol.margin", { fallback: "Margin" })}
                    {__sCInd("theoROI")}
                  </th>
                  <th style={__thStR} onClick={() => { __cycleCSort("marginPerH"); }}>
                    {t("costs.cocol.margin_per_h", { fallback: "Margin/h" })}
                    {__sCInd("marginPerH")}
                  </th>
                  <th style={__thStR} onClick={() => { __cycleCSort("theoROIPct"); }} title={t("costs.cocol.theo_roi_pct_title", { fallback: "Margin ÷ invoiced ex VAT" })}>
                    {t("costs.cocol.theo_roi_pct")}
                    {__sCInd("theoROIPct")}
                  </th>
                  <th style={__thStR} onClick={() => { __cycleCSort("amtPaid"); }} title={t("costs.cocol.amount_paid_ex_vat_title", { fallback: "The matters' payments ex VAT, pro-rated over this collaborator's share of what was invoiced" })}>
                    {t("costs.cocol.amount_paid_ex_vat", { fallback: "Paid ex VAT" })}
                    {__sCInd("amtPaid")}
                  </th>
                  <th style={__thStR} onClick={() => { __cycleCSort("realROI"); }} title={t("costs.cocol.effective_margin_title", { fallback: "Paid ex VAT − amount paid to the collaborator" })}>
                    {t("costs.cocol.effective_margin", { fallback: "Effective margin" })}
                    {__sCInd("realROI")}
                  </th>
                  <th style={__thStR} onClick={() => { __cycleCSort("realMarginPerH"); }}>
                    {t("costs.cocol.effective_margin_per_h", { fallback: "Effective margin/h" })}
                    {__sCInd("realMarginPerH")}
                  </th>
                  <th style={__thStR} onClick={() => { __cycleCSort("realROIPct"); }} title={t("costs.cocol.effective_roi_pct_title", { fallback: "Effective margin ÷ paid ex VAT" })}>
                    {t("costs.cocol.effective_roi_pct", { fallback: "% effective ROI" })}
                    {__sCInd("realROIPct")}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  className="costs-table-total"
                  /* 195B §B — the ground is the class rule's `var(--as)`
                     (App.css); see the first table's total row. */
                  style={{
                    borderBottom: "2px solid var(--ac)",
                    fontWeight: 700,
                  }}
                >
                  {/* 200 §G.1 — spans name + role (`sticky-col-12`), as the
                      case table's label does since 199 B.0; the 145-px name
                      column no longer carries a nowrap label wider than itself. */}
                  <td
                    className="sticky-col-12"
                    colSpan={2}
                    style={{
                      padding: "4px 6px",
                      color: "var(--ac)",
                      fontSize: FS.s10,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      fontWeight: 700,
                    }}
                  >
                    {t("costs.grand_total")}{" "}
                    <span
                      style={{
                        fontWeight: 400,
                        opacity: 0.7,
                        textTransform: "none",
                      }}
                    >
                      (
                      {t("costs.collaborators_count", {
                        count: __collabSorted.length,
                      })}
                      )
                    </span>
                  </td>
                  <td className="mono" style={{ ...__tdR, color: "var(--ac)" }}>
                    {__hCo(__cgtOv.totalMin)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: "var(--ac)" }}>
                    {__fcCo(__cgtOv.cost)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: "var(--ac)" }}>
                    {__fcCo(__cgtOv.amtInvoiced)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: __retClr(__cgtOv.theoROI) }}>
                    {__fcCo(__cgtOv.theoROI)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: __retClr(__cgtOv.marginPerH) }}>
                    {__ratCo(__cgtOv.marginPerH)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: __retClr(__cgtOv.theoROIPct) }}>
                    {__pctCo(__cgtOv.theoROIPct)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: "var(--ac)" }}>
                    {__fcCo(__cgtOv.amtPaid)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: __retClr(__cgtOv.realROI) }}>
                    {__fcCo(__cgtOv.realROI)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: __retClr(__cgtOv.realMarginPerH) }}>
                    {__ratCo(__cgtOv.realMarginPerH)}
                  </td>
                  <td className="mono" style={{ ...__tdR, color: __retClr(__cgtOv.realROIPct) }}>
                    {__pctCo(__cgtOv.realROIPct)}
                  </td>
                </tr>
                {__collabSorted.map((r) => {
                  const ov = __ovOf(r);
                  return (
                    <tr key={r.collab.id}>
                      {/* 199 §C.2 — the two fixed cells, the one definition the
                          activity table above reads (the `{...({} as any)}` no-op
                          spread that stood on the badge — 187's census item 8 —
                          went with the move, as that item prescribes). */}
                      {collabFixedCells(r.collab, currentUser)}
                      <td className="mono" style={__tdR}>
                        {__hCo(ov.totalMin)}
                      </td>
                      {/* 161B §6's rule, one table over: a computed figure renders
                          as money, zero included — the `x > 0 ? x : null` guards
                          that stood on these cells turned a measured 0 into
                          "absent"; `__fcCo(null)` is already "—". */}
                      <td className="mono" style={__tdR}>
                        {__fcCo(ov.cost)}
                      </td>
                      <td className="mono" style={__tdR}>
                        {__fcCo(ov.amtInvoiced)}
                      </td>
                      <td className="mono" style={{ ...__tdR, color: __retClr(ov.theoROI), fontWeight: 600 }}>
                        {__fcCo(ov.theoROI)}
                      </td>
                      <td className="mono" style={{ ...__tdR, color: __retClr(ov.marginPerH), fontWeight: 600 }}>
                        {__ratCo(ov.marginPerH)}
                      </td>
                      <td className="mono" style={{ ...__tdR, color: __retClr(ov.theoROIPct), fontWeight: 600 }}>
                        {__pctCo(ov.theoROIPct)}
                      </td>
                      <td className="mono" style={__tdR}>
                        {__fcCo(ov.amtPaid)}
                      </td>
                      <td className="mono" style={{ ...__tdR, color: __retClr(ov.realROI), fontWeight: 600 }}>
                        {__fcCo(ov.realROI)}
                      </td>
                      <td className="mono" style={{ ...__tdR, color: __retClr(ov.realMarginPerH), fontWeight: 600 }}>
                        {__ratCo(ov.realMarginPerH)}
                      </td>
                      <td className="mono" style={{ ...__tdR, color: __retClr(ov.realROIPct), fontWeight: 600 }}>
                        {__pctCo(ov.realROIPct)}
                      </td>
                    </tr>
                  );
                })}
                {__collabSorted.length === 0 && (
                  <tr>
                    <td
                      colSpan={12}
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "var(--tm)",
                        fontStyle: "italic",
                      }}
                    >
                      {t("costs.no_collaborator_activity")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
        {/* 161 §9 — the overheads sheet, LAST since 199 §E.1. Admin-only, like
            the write path it saves through. State lifted to CoView so a typed
            cell reaches the next costs compute without a refetch. */}
        {_appIsAdmin && ohSheet !== null && <OverheadsSheet t={t} sheet={ohSheet} setSheet={setOhSheet} firmName={__firmName} dayTheme={dayTheme} appFont={appFont} onSaved={() => { setOhRev((n) => n + 1); }} />}
        {/* 199 §C.3 [RULED — Laurent: "still remove old and stale
            'administrative workload' section"] — the admin-workload table
            (name · role · hours · % of the secretaries' total · the five
            SEC_TASKS buckets as hours and %) STOOD HERE and is GONE, with its
            state, its sort, its server block and the literal bucket list on
            the server; App's own SEC_TASKS outlived it by one brief — 200 §E
            made the modal's secretarial list the taxonomy's own flag
            (meta.secretarial), so no literal is left. The `costs.scol.*`
            keys, `costs.section.admin_workload`, `costs.secretaries_count`
            and `costs.no_secretary_activity` lose their readers — rows kept
            for the retired-rows one-shot (C.4). */}
      </div>
    );
};

export default CoView;
